import consts from "../consts";
import utils from "../utils";
import lang from "../lang";

// import { Howler } from "howler";

import {
  Container,
  Sprite,
  Texture,
  Text,
  AnimatedSprite,
  SCALE_MODES,
} from "pixi.js";
import Vue from "vue/dist/vue.min";
import Reel from "../objects/Reel";
import Symbol from "../objects/Symbol";
import gsap from "gsap";
import gsapPixiPlugin from "gsap/PixiPlugin";
import { CustomEase } from "gsap/CustomEase";
import { MotionBlurFilter } from "@pixi/filter-motion-blur";
gsapPixiPlugin.registerPIXI(window.PIXI);
gsap.registerPlugin(gsapPixiPlugin, CustomEase);

// import notificationScheduler from "../notificationScheduler";
import _ from "lodash";
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import auth from "../services/auth";

const firebaseConfig = {
  apiKey: "AIzaSyBIInvn22bDjUh6NPRKNHQOFnrDkS-DNJg",
  authDomain: "turn-holdem-poker.firebaseapp.com",
  databaseURL: "https://turn-holdem-poker.firebaseio.com",
  projectId: "turn-holdem-poker",
  storageBucket: "turn-holdem-poker.appspot.com",
  messagingSenderId: "68783598739",
  appId: "1:68783598739:web:9d3f08a8c3a651bbfcb166",
  measurementId: "G-PT5ZY7XFMT",
};

const firebase = initializeApp(firebaseConfig);

const analytics = getAnalytics();

const SCENE_NAME = "GAME";

export default class Game extends Container {
  constructor({ loader, resources, sounds, variationConfig, user }) {
    super();

    this.user = user;

    this.settings = {
      chosenLines: consts.game.chosenLines,
      betAmount: variationConfig.allowedBets[2],
      gameMode: consts.game.gameMode.DEFAULT,
      autoPlayCount: 0,
      spinMode: consts.game.spinMode.DEFAULT,
      mute: false,
    };

    this.oncePlayed = false;
    this.name = SCENE_NAME;
    this.locale = "en";
    this.loader = loader;
    this.resources = resources;
    this.sounds = sounds;
    this.selectedSpinMode = "normal";
    this.gameType = "normal";
    this.variationConfig = variationConfig;
    this.jackpots = {
      minor:
        this.settings.betAmount *
        this.settings.chosenLines *
        this.variationConfig.jackpotMultipliers.minor,
      major:
        this.settings.betAmount *
        this.settings.chosenLines *
        this.variationConfig.jackpotMultipliers.major,
      grand:
        this.settings.betAmount *
        this.settings.chosenLines *
        this.variationConfig.jackpotMultipliers.grand,
    };
    this.currentBonusTotalWin = 0;
    this.startingFreeSpinCount = 0;
    this.currentFreeSpinCount = 0;
    this.fogPositions = [
      [200, 200],
      [200, 600],
      [200, 1000],
      [600, 200],
      [600, 600],
      [600, 1000],
      [1000, 200],
      [1000, 600],
      [1000, 1000],
      [1400, 200],
      [1400, 600],
      [1400, 1000],
      [1800, 200],
      [1800, 600],
      [1800, 1000],
    ];
    this.settings.betAmount = this.variationConfig.allowedBets[2];
    this.alpha = 0;
    this.reelSpinAnimations = [];
    this.playingSounds = [];
    this.autoButtonsShowing = false;
    this.isSpinSkipped = false;
    this._isSpinAvailable = true;
    this._isAutoPlayRunning = false;
    this.initialize();
  }

  getDisplayedBalance(amount = this.user.chips) {
    return Number(amount);
  }

  getBalanceSuffix(amount = this.user.chips) {
    const displayedAmount = Math.abs(Number(this.getDisplayedBalance(amount)));

    if (displayedAmount >= 1000000000000) return "T";
    if (displayedAmount >= 1000000000) return "B";
    if (displayedAmount >= 1000000) return "M";
    if (displayedAmount >= 1000) return "K";

    return "";
  }

  getFormattedBalance(amount = this.user.chips) {
    const formattedAmount = utils
      .getFormattedAmount(Number(this.getDisplayedBalance(amount)).toFixed(0))
      .toString();
    const suffix = this.getBalanceSuffix(amount);

    return suffix ? `${formattedAmount} ${suffix}` : formattedAmount;
  }

  updateBalanceText(amount = this.user.chips) {
    const uiContainer = this.getChildByName("UI_CONTAINER");
    if (!uiContainer) return;

    const balanceContainer = uiContainer.getChildByName("BALANCE_CONTAINER");
    if (!balanceContainer) return;

    const balanceBg = balanceContainer.getChildByName("BALANCE_BG");
    const balanceText = balanceBg.getChildByName("BALANCE_TEXT");
    const buyButton = balanceContainer.getChildByName("BUY_BUTTON");

    balanceText.text = this.getFormattedBalance(amount);
    balanceText.scale.set(1);

    const leftPadding = balanceBg.height * 0.9;
    const rightPadding = balanceBg.height * 0.25;
    const fallbackMaxWidth = balanceBg.width * 0.6;

    let maxWidth = fallbackMaxWidth;

    if (buyButton) {
      const centerX = balanceBg.x + balanceText.x;
      const leftEdge = balanceBg.x - balanceBg.width / 2 + leftPadding;
      const rightEdge = buyButton.x - buyButton.width / 2 - rightPadding;
      const leftSpace = centerX - leftEdge;
      const rightSpace = rightEdge - centerX;

      if (leftSpace > 0 && rightSpace > 0) {
        maxWidth = Math.max(fallbackMaxWidth, 2 * Math.min(leftSpace, rightSpace));
      }
    }

    if (balanceText.width > maxWidth) {
      const fitScale = maxWidth / balanceText.width;
      balanceText.scale.set(fitScale, fitScale);
    }
  }

  set isSpinAvailable(status) {
    this._isSpinAvailable = status;
    const spinButtonGraphic = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("SPIN_BUTTON");
    spinButtonGraphic.alpha = status ? 1 : 0.6;
  }

  get isSpinAvailable() {
    return this._isSpinAvailable;
  }

  async initialize() {
    this.sounds["background_slow"].volume(0.3);
    this.sounds["background_slow"].loop(true);
    this.sounds["background_slow"].play();
    let params = new URLSearchParams(window.location.search);
    if (params.get("lang_local") && params.get("lang_local") !== "ru") {
      this.locale = params.get("lang_local");
    }
    this.createSlotBoard();
    this.createUI();
    window.addEventListener("message", (d) => {
      if (d.data === "handleSound") {
        this.handleSoundButtonClicked(this.settings.mute);
      }
    });

    this.fadeIn();
  }

  fadeIn() {
    gsap.to(this, {
      duration: 0.1,
      alpha: 1,
      ease: "linear",
    });
  }

  createSlotBoard() {
    const slotBoardContainer = new Container();

    slotBoardContainer.name = "SLOT_BOARD";
    slotBoardContainer.width = 1690;
    slotBoardContainer.height = 1038;
    slotBoardContainer.x = consts.game.width / 2;
    slotBoardContainer.y = consts.game.height / 2;
    slotBoardContainer.y -= consts.game.height * 0.115;
    const slotBoardRatio =
      Math.min(window.innerWidth / 1690, window.innerHeight / 1038) -
      (utils.isMobile ? 0.05 : 0.04);

    slotBoardContainer.scale.set(slotBoardRatio, slotBoardRatio);

    this.addChild(slotBoardContainer);

    const symbolFrameBg = new Sprite(this.resources["symbolFrameBg"].texture);
    symbolFrameBg.name = "SYMBOL_FRAME_BG";
    symbolFrameBg.anchor.set(0.5);
    symbolFrameBg.y += 42;
    slotBoardContainer.addChild(symbolFrameBg);

    const reelsContainer = new Container();

    reelsContainer.name = "REELS_CONTAINER";
    reelsContainer.x = 10;
    reelsContainer.y = -85;
    reelsContainer.width = 1250;
    reelsContainer.height = 750;

    const mask = new Sprite(Texture.WHITE);

    mask.x = -625;
    mask.y = -125;
    mask.width = 1250;
    mask.height = 750;
    reelsContainer.addChild(mask);
    reelsContainer.mask = mask;

    slotBoardContainer.addChild(reelsContainer);

    this.createReels("normal");

    const jackpotContainer = new Container();
    jackpotContainer.name = "JACKPOT_CONTAINER";
    jackpotContainer.width = 1015;
    jackpotContainer.height = 122;
    jackpotContainer.x = -505;
    jackpotContainer.y = -410;

    const majorJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.major).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 25,
        fontWeight: 500,
        fill: ["#b28233", "#f7cc64", "#b28233"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#6c3207",
        strokeThickness: 4,
        dropShadow: true,
        dropShadowBlur: 0,
        dropShadowDistance: 2,
      }
    );
    majorJackpotAmount.name = "MAJOR_JACKPOT_AMOUNT";
    majorJackpotAmount.x = 190;
    majorJackpotAmount.y = 120;
    majorJackpotAmount.anchor.x = 0.5;

    const minorJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.minor).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 25,
        fontWeight: 500,
        fill: ["#b28233", "#f7cc64", "#b28233"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#6c3207",
        strokeThickness: 4,
        dropShadow: true,
        dropShadowBlur: 0,
        dropShadowDistance: 2,
      }
    );
    minorJackpotAmount.name = "MINOR_JACKPOT_AMOUNT";
    minorJackpotAmount.x = 845;
    minorJackpotAmount.y = 120;
    minorJackpotAmount.anchor.x = 0.5;

    const grandJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.grand).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 35,
        fontWeight: 500,
        fill: ["#b28233", "#f7cc64", "#b28233"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#6c3207",
        strokeThickness: 4,
        dropShadow: true,
        dropShadowBlur: 0,
        dropShadowDistance: 2,
      }
    );
    grandJackpotAmount.name = "GRAND_JACKPOT_AMOUNT";
    grandJackpotAmount.x = 510;
    grandJackpotAmount.y = 68;
    grandJackpotAmount.anchor.x = 0.5;

    jackpotContainer.addChild(minorJackpotAmount);
    jackpotContainer.addChild(majorJackpotAmount);
    jackpotContainer.addChild(grandJackpotAmount);

    slotBoardContainer.addChild(jackpotContainer);

    const winLayoutContainer = new Container();
    winLayoutContainer.name = "WIN_LAYOUT_CONTAINER";
    winLayoutContainer.alpha = 0;

    const niceWin = new AnimatedSprite(
      this.resources["nice"].spritesheet.animations["nice"]
    );
    niceWin.name = "NICE";
    niceWin.loop = false;
    niceWin.animationSpeed = 0.5;
    niceWin.anchor.set(0.5);
    niceWin.y = 0;
    niceWin.scale.set(1.25, 1.25);
    niceWin.alpha = 0;

    const superWin = new AnimatedSprite(
      this.resources["super"].spritesheet.animations["super"]
    );
    superWin.name = "SUPER";
    superWin.loop = false;
    superWin.animationSpeed = 0.5;
    superWin.anchor.set(0.5);
    superWin.y = 0;
    superWin.scale.set(1.24, 1.24);
    superWin.alpha = 0;

    const sensationalWin = new AnimatedSprite(
      this.resources["sensational"].spritesheet.animations["sensational"]
    );
    sensationalWin.name = "SENSATIONAL";
    sensationalWin.loop = false;
    sensationalWin.animationSpeed = 0.5;
    sensationalWin.anchor.set(0.5);
    sensationalWin.y = 0;
    sensationalWin.scale.set(1.2, 1.2);
    sensationalWin.alpha = 0;

    winLayoutContainer.addChild(niceWin);
    winLayoutContainer.addChild(superWin);
    winLayoutContainer.addChild(sensationalWin);

    const winAmountText = new Text("", {
      fontFamily: "Reggae One",
      fontSize: 70,
      fill: ["#FDC100", "#FFFE15", "#FDC100"],
      fillGradientStops: [0, 0.66, 1],
      align: "center",
      stroke: "#520D0D",
      strokeThickness: 3,
    });
    winAmountText.name = "WIN_AMOUNT_TEXT";
    winAmountText.anchor.set(0.5);
    winAmountText.y += 30;
    winAmountText.texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;
    winLayoutContainer.addChild(winAmountText);

    slotBoardContainer.addChild(winLayoutContainer);
  }

  createUI() {
    const uiContainer = new Container();
    uiContainer.name = "UI_CONTAINER";
    uiContainer.x = 0;
    uiContainer.y = 0;

    const controlPanelContainer = new Container();
    controlPanelContainer.name = "CONTROL_PANEL_CONTAINER";
    controlPanelContainer.x = 0;
    controlPanelContainer.y = consts.game.height;

    const controlPanelBg = new Sprite(this.resources["panel-bg"].texture);
    controlPanelBg.name = "CONTROL_PANEL_BG";
    controlPanelBg.anchor.set(0.5);
    controlPanelBg.width = consts.game.width;
    controlPanelBg.height = consts.game.width * 0.069;
    controlPanelBg.x = consts.game.width / 2;
    controlPanelBg.y = -(controlPanelBg.height / 2);

    controlPanelContainer.addChild(controlPanelBg);

    const controlPanelLeft = new Container();
    controlPanelLeft.name = "CONTROL_PANEL_LEFT_CONTAINER";
    controlPanelLeft.x = 0;
    controlPanelLeft.y = 0;

    controlPanelContainer.addChild(controlPanelLeft);

    const infoButton = new Sprite(this.resources["button-info"].texture);
    infoButton.name = "INFO_BUTTON";
    infoButton.anchor.set(0.5);
    infoButton.width = consts.game.width * 0.046;
    infoButton.height = consts.game.width * 0.046;
    infoButton.x = consts.game.width * 0.046;
    infoButton.y = -consts.game.width * 0.028;
    infoButton.interactive = true;
    infoButton.buttonMode = true;

    this.elementEventHandler(
      infoButton,
      () => this.onInfoButtonClicked(),
      true
    );

    const betBg = new Sprite(this.resources["button-bet-bg"].texture);
    betBg.name = "BET_BG";
    betBg.anchor.set(0.5);
    betBg.width = consts.game.width * 0.179;
    betBg.height = consts.game.width * 0.046;
    betBg.x = consts.game.width * 0.187;
    betBg.y = -consts.game.width * 0.028;

    const betText = new Text(
      utils
        .getShortFormat(this.settings.betAmount * this.settings.chosenLines)
        .toString(),
      {
        fontFamily: "Arial",
        fontSize: consts.game.width * 0.018,
        fill: "#ffffff",
        align: "center",
        fontWeight: "bold",
      }
    );
    betText.name = "BET_TEXT";
    betText.x = consts.game.width * 0.184;
    betText.y = -consts.game.width * 0.028;
    betText.anchor.set(0.5);

    const betMinus = new Sprite(this.resources["button-bet-minus"].texture);
    betMinus.name = "BET_MINUS";
    betMinus.anchor.set(0.5);
    betMinus.width = consts.game.width * 0.033;
    betMinus.height = consts.game.width * 0.041;
    betMinus.x = consts.game.width * 0.117;
    betMinus.y = -consts.game.width * 0.028;
    betMinus.interactive = true;
    betMinus.buttonMode = true;

    this.elementEventHandler(betMinus, () => this.changeBetAmount(-1), false);

    const betPlus = new Sprite(this.resources["button-bet-plus"].texture);
    betPlus.name = "BET_PLUS";
    betPlus.anchor.set(0.5);
    betPlus.width = consts.game.width * 0.033;
    betPlus.height = consts.game.width * 0.041;
    betPlus.x = consts.game.width * 0.256;
    betPlus.y = -consts.game.width * 0.028;
    betPlus.interactive = true;
    betPlus.buttonMode = true;

    this.elementEventHandler(betPlus, () => this.changeBetAmount(1), false);

    const maxbetButton = new Sprite(this.resources["button-maxbet"].texture);
    maxbetButton.name = "MAXBET_BUTTON";
    maxbetButton.anchor.set(0.5);
    maxbetButton.width = consts.game.width * 0.07;
    maxbetButton.height = consts.game.width * 0.046;
    maxbetButton.x = consts.game.width * 0.335;
    maxbetButton.y = -consts.game.width * 0.028;
    maxbetButton.interactive = true;
    maxbetButton.buttonMode = true;

    this.elementEventHandler(
      maxbetButton,
      () => this.onMaxbetButtonClicked(),
      false
    );

    controlPanelLeft.addChild(betBg);
    controlPanelLeft.addChild(betText);
    controlPanelLeft.addChild(betMinus);
    controlPanelLeft.addChild(betPlus);
    controlPanelLeft.addChild(maxbetButton);
    controlPanelLeft.addChild(infoButton);

    const panelTextFontSize = consts.game.width * 0.019;
    const winAmountText = new Text("GOOD LUCK!", {
      fontFamily: "Reggae One",
      fontSize: panelTextFontSize,
      fill: ["#FDC100", "#FFFE15", "#FDC100"],
      fillGradientStops: [0, 0.66, 1],
      align: "center",
      stroke: "#520D0D",
      strokeThickness: 3,
    });
    winAmountText.x = consts.game.width * 0.5;
    winAmountText.y = -consts.game.width * 0.026;
    winAmountText.name = "WIN_AMOUNT_TEXT";
    winAmountText.anchor.set(0.5);
    winAmountText.texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;

    controlPanelContainer.addChild(winAmountText);

    const controlPanelRight = new Container();
    controlPanelRight.name = "CONTROL_PANEL_RIGHT_CONTAINER";
    controlPanelRight.x = 0;
    controlPanelRight.y = 0;

    controlPanelContainer.addChild(controlPanelRight);

    const fastButton = new Sprite(this.resources["button-fast"].texture);
    fastButton.name = "FAST_BUTTON";
    fastButton.anchor.set(0.5);
    fastButton.width = consts.game.width * 0.07;
    fastButton.height = consts.game.width * 0.046;
    fastButton.x = consts.game.width * 0.718;
    fastButton.y = -consts.game.width * 0.028;
    fastButton.interactive = true;
    fastButton.buttonMode = true;

    this.elementEventHandler(
      fastButton,
      () => this.onFastButtonClicked(),
      false
    );

    const spinBg = new Sprite(this.resources["spin-bg"].texture);
    spinBg.name = "SPIN_BG";
    spinBg.anchor.set(0.5);
    spinBg.width = consts.game.width * 0.152;
    spinBg.height = consts.game.width * 0.046;
    spinBg.x = consts.game.width * 0.877;
    spinBg.y = -consts.game.width * 0.028;
    spinBg.visible = false;

    const remainingSpinText = new Text("Free Spins 10 / 15", {
      fontFamily: "Arial",
      fontSize: 30,
      fill: "#ffffff",
      align: "center",
      fontWeight: "bold",
    });
    remainingSpinText.name = "REMAINING_SPIN_TEXT";
    remainingSpinText.x = 0;
    remainingSpinText.y = 0;
    remainingSpinText.anchor.set(0.5);

    spinBg.addChild(remainingSpinText);

    controlPanelRight.addChild(spinBg);

    const spinButton = new Sprite(this.resources["button-spin"].texture);
    spinButton.name = "SPIN_BUTTON";
    spinButton.anchor.set(0.5);
    spinButton.width = consts.game.width * 0.152;
    spinButton.height = consts.game.width * 0.046;
    spinButton.x = consts.game.width * 0.877;
    spinButton.y = -consts.game.width * 0.028;
    spinButton.interactive = true;
    spinButton.buttonMode = true;

    const spinButtonContainerOriginalScale = spinButton.scale.x;

    spinButton.on("click", () => {
      if (!this.isSpinAvailable || this.autoButtonsShowing) return false;
      this.onSpinButtonClicked();
    });
    spinButton.on("tap", () => {
      if (!this.isSpinAvailable || this.autoButtonsShowing) return false;
      this.sounds["ui"].play();
      this.onSpinButtonClicked();
    });
    spinButton.on("mouseover", () => {
      const scaleAmount =
        spinButtonContainerOriginalScale +
        spinButtonContainerOriginalScale * 0.1;
      if (this.isSpinAvailable) {
        spinButton.scale.set(scaleAmount, scaleAmount);
        this.sounds["ui"].play();
      }
    });
    spinButton.on("mouseout", () => {
      spinButton.scale.set(
        spinButtonContainerOriginalScale,
        spinButtonContainerOriginalScale
      );
    });
    let timer;
    const mouseUp = () => {
      if (timer) clearTimeout(timer);
    };
    const mouseDown = () => {
      mouseUp();
      if (this.isSpinAvailable) {
        timer = setTimeout(() => {
          this.onLongTapSpinButton();
        }, 500);
      }
    };
    const touchStart = () => {
      if (!timer && this.isSpinAvailable) {
        timer = setTimeout(() => {
          this.onLongTapSpinButton();
        }, 500);
      }
    };
    const touchEnd = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    spinButton.on("touchstart", touchStart);
    spinButton.on("touchend", touchEnd);
    spinButton.on("mousedown", mouseDown);
    document.body.addEventListener("mouseup", mouseUp);

    const stopButton = new Sprite(this.resources["button-stop"].texture);
    stopButton.name = "STOP_BUTTON";
    stopButton.anchor.set(0.5);
    stopButton.width = consts.game.width * 0.152;
    stopButton.height = consts.game.width * 0.046;
    stopButton.x = consts.game.width * 0.877;
    stopButton.y = -consts.game.width * 0.028;
    stopButton.interactive = true;
    stopButton.buttonMode = true;
    stopButton.visible = false;

    const stopButtonScale = stopButton.scale.x;

    stopButton.on("click", () => {
      this.onStopButtonClicked();
    });
    stopButton.on("tap", () => {
      this.sounds["ui"].play();
      this.onStopButtonClicked();
    });
    stopButton.on("mouseover", () => {
      const scaleAmount = stopButtonScale + stopButtonScale * 0.1;
      if (this.isSpinAvailable) {
        stopButton.scale.set(scaleAmount, scaleAmount);
        this.sounds["ui"].play();
      }
    });
    stopButton.on("mouseout", () => {
      stopButton.scale.set(stopButtonScale, stopButtonScale);
    });

    const stopButtonText = new Text("", {
      fontFamily: "Arial",
      fontSize: 25,
      fill: "#ffffff",
      align: "center",
      fontWeight: "bold",
    });

    stopButtonText.name = "STOP_BUTTON_TEXT";
    stopButtonText.x = -90;
    stopButtonText.y = 0;
    stopButtonText.anchor.set(0.5);

    controlPanelRight.addChild(spinButton);
    controlPanelRight.addChild(stopButton);
    stopButton.addChild(stopButtonText);
    controlPanelRight.addChild(fastButton);

    const autoButtonsContainer = new Container();
    autoButtonsContainer.name = "AUTO_BUTTONS_CONTAINER";
    autoButtonsContainer.x = 0;
    autoButtonsContainer.y = 0;
    autoButtonsContainer.visible = false;

    const autoBg = new Sprite(this.resources["button-auto-spin-bg"].texture);
    autoBg.name = "AUTO_BG";
    autoBg.anchor.set(0.5);
    autoBg.width = consts.game.width * 0.1729;
    autoBg.height = consts.game.width * 0.3114;
    autoBg.x = consts.game.width * 0.877;
    autoBg.y = -consts.game.width * 0.2031;

    const arrowButton = new Sprite(this.resources["down-arrow"].texture);
    arrowButton.name = "ARROW_BUTTON";
    arrowButton.anchor.set(0.5);
    arrowButton.width = consts.game.width * 0.127;
    arrowButton.height = consts.game.width * 0.041;
    arrowButton.x = consts.game.width * 0.877;
    arrowButton.y = -consts.game.width * 0.3661;
    arrowButton.interactive = true;
    arrowButton.buttonMode = true;

    this.elementEventHandler(
      arrowButton,
      () => this.closeAutoContainer(),
      false
    );

    const auto10 = new Sprite(this.resources["button-auto-spin-10"].texture);
    auto10.name = "AUTO_10";
    auto10.anchor.set(0.5);
    auto10.width = consts.game.width * 0.097;
    auto10.height = consts.game.width * 0.035;
    auto10.x = consts.game.width * 0.877;
    auto10.y = -consts.game.width * 0.083;
    auto10.interactive = true;
    auto10.buttonMode = true;

    this.elementEventHandler(auto10, () => this.onAutoButtonClicked(10), false);

    const auto25 = new Sprite(this.resources["button-auto-spin-25"].texture);
    auto25.name = "AUTO_25";
    auto25.anchor.set(0.5);
    auto25.width = consts.game.width * 0.104;
    auto25.height = consts.game.width * 0.035;
    auto25.x = consts.game.width * 0.877;
    auto25.y = -consts.game.width * 0.125;
    auto25.interactive = true;
    auto25.buttonMode = true;

    this.elementEventHandler(auto25, () => this.onAutoButtonClicked(25), false);

    const auto50 = new Sprite(this.resources["button-auto-spin-50"].texture);
    auto50.name = "AUTO_50";
    auto50.anchor.set(0.5);
    auto50.width = consts.game.width * 0.11;
    auto50.height = consts.game.width * 0.035;
    auto50.x = consts.game.width * 0.877;
    auto50.y = -consts.game.width * 0.166;
    auto50.interactive = true;
    auto50.buttonMode = true;

    this.elementEventHandler(auto50, () => this.onAutoButtonClicked(50), false);

    const auto100 = new Sprite(this.resources["button-auto-spin-100"].texture);
    auto100.name = "AUTO_100";
    auto100.anchor.set(0.5);
    auto100.width = consts.game.width * 0.117;
    auto100.height = consts.game.width * 0.035;
    auto100.x = consts.game.width * 0.877;
    auto100.y = -consts.game.width * 0.208;
    auto100.interactive = true;
    auto100.buttonMode = true;

    this.elementEventHandler(
      auto100,
      () => this.onAutoButtonClicked(100),
      false
    );

    const auto500 = new Sprite(this.resources["button-auto-spin-500"].texture);
    auto500.name = "AUTO_500";
    auto500.anchor.set(0.5);
    auto500.width = consts.game.width * 0.1255;
    auto500.height = consts.game.width * 0.0421;
    auto500.x = consts.game.width * 0.877;
    auto500.y = -consts.game.width * 0.2541;
    auto500.interactive = true;
    auto500.buttonMode = true;

    this.elementEventHandler(
      auto500,
      () => this.onAutoButtonClicked(500),
      false
    );

    const auto1000 = new Sprite(
      this.resources["button-auto-spin-1000"].texture
    );
    auto1000.name = "AUTO_1000";
    auto1000.anchor.set(0.5);
    auto1000.width = consts.game.width * 0.1317;
    auto1000.height = consts.game.width * 0.0427;
    auto1000.x = consts.game.width * 0.877;
    auto1000.y = -consts.game.width * 0.3036;
    auto1000.interactive = true;
    auto1000.buttonMode = true;

    this.elementEventHandler(
      auto1000,
      () => this.onAutoButtonClicked(1000),
      false
    );

    autoButtonsContainer.addChild(autoBg);
    autoButtonsContainer.addChild(arrowButton);
    autoButtonsContainer.addChild(auto10);
    autoButtonsContainer.addChild(auto25);
    autoButtonsContainer.addChild(auto50);
    autoButtonsContainer.addChild(auto100);
    autoButtonsContainer.addChild(auto500);
    autoButtonsContainer.addChild(auto1000);

    controlPanelRight.addChild(autoButtonsContainer);

    uiContainer.addChild(controlPanelContainer);

    const buyBonusContainer = new Container();
    buyBonusContainer.name = "BUY_BONUS_CONTAINER";
    buyBonusContainer.x = 0;
    buyBonusContainer.y = 0;

    const buyBonusScale = Math.min(
      window.innerWidth / 1920,
      window.innerHeight / 1080
    );

    buyBonusContainer.scale.set(buyBonusScale, buyBonusScale);

    const buyBonusButton = new Sprite(this.resources["buy-bonus"].texture);
    buyBonusButton.name = "BUY_BONUS_BUTTON";
    buyBonusButton.anchor.set(0.5);
    buyBonusButton.width = 230;
    buyBonusButton.height = 155;
    buyBonusButton.x = 140;
    buyBonusButton.y = 745;
    buyBonusButton.interactive = true;
    buyBonusButton.buttonMode = true;

    this.elementEventHandler(
      buyBonusButton,
      () => this.onBuyBonusButtonClicked(),
      false
    );

    buyBonusContainer.addChild(buyBonusButton);

    const buyBonusButtonText = new Text("BUY BONUS", {
      fontFamily: "Reggae One",
      fontSize: 35,
      fontWeight: "bold",
      fill: ["#FDC100", "#FFFE15", "#FDC100"],
      fillGradientStops: [0, 0.66, 1],
      align: "center",
      stroke: "#520D0D",
      strokeThickness: 3,
      wordWrap: true,
      wordWrapWidth: 210,
    });
    buyBonusButtonText.name = "BUY_BONUS_TEXT";
    buyBonusButtonText.x = 0;
    buyBonusButtonText.y = -40;
    buyBonusButtonText.anchor.set(0.5);

    const buyBonusButtonAmount = new Text(
      utils.getShortFormat(
        this.settings.betAmount *
          this.settings.chosenLines *
          this.variationConfig.bonus.multiplier
      ),
      {
        fontFamily: "Reggae One",
        fontSize: 45,
        fontWeight: "bold",
        fill: ["#FDC100", "#FFFE15", "#FDC100"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#520D0D",
        strokeThickness: 3,
      }
    );
    buyBonusButtonAmount.name = "BUY_BONUS_AMOUNT";
    buyBonusButtonAmount.x = 0;
    buyBonusButtonAmount.y = 50;
    buyBonusButtonAmount.anchor.set(0.5);

    buyBonusButton.addChild(buyBonusButtonText);
    buyBonusButton.addChild(buyBonusButtonAmount);

    this.addChild(buyBonusContainer);

    const balanceContainer = new Container();
    balanceContainer.name = "BALANCE_CONTAINER";
    balanceContainer.x = utils.isMobile ? 10 : 0;
    balanceContainer.y = 0;

    const balanceBg = new Sprite(this.resources["chip-panel-bg"].texture);
    balanceBg.name = "BALANCE_BG";
    balanceBg.anchor.set(0.5);
    balanceBg.width = consts.game.width * 0.222;
    balanceBg.height = consts.game.width * 0.037;
    balanceBg.x = consts.game.width * 0.115;
    balanceBg.y = consts.game.width * 0.023;

    balanceContainer.addChild(balanceBg);

    const balanceText = new Text("", {
      fontFamily: "Arial",
      fontSize: 35,
      fill: "#ffffff",
      align: "center",
      fontWeight: "bold",
    });
    balanceText.name = "BALANCE_TEXT";
    balanceText.x = -consts.game.width * 0.005;
    balanceText.y = 0;
    balanceText.anchor.set(0.5);
    balanceBg.addChild(balanceText);

    const buyButton = new Sprite(this.resources["button-buy-chip"].texture);
    buyButton.name = "BUY_BUTTON";
    buyButton.anchor.set(0.5);
    buyButton.width = consts.game.width * 0.045;
    buyButton.height = consts.game.width * 0.033;
    buyButton.x = consts.game.width * 0.202;
    buyButton.y = consts.game.width * 0.024;
    buyButton.interactive = true;
    buyButton.buttonMode = true;

    this.elementEventHandler(buyButton, () => this.onBuyChipClicked(), false);

    balanceContainer.addChild(buyButton);

    uiContainer.addChild(controlPanelContainer);

    uiContainer.addChild(balanceContainer);

    const menuContainer = new Container();
    menuContainer.name = "MENU_CONTAINER";
    menuContainer.x = utils.isMobile ? -10 : 0;
    menuContainer.y = 0;

    const menuButton = new Sprite(this.resources["button-menu"].texture);
    menuButton.name = "MENU_BUTTON";
    menuButton.anchor.set(0.5);
    menuButton.width = consts.game.width * 0.046;
    menuButton.height = consts.game.width * 0.046;
    menuButton.x = consts.game.width * 0.9;
    menuButton.y = consts.game.width * 0.028;
    menuButton.interactive = true;
    menuButton.buttonMode = true;

    this.elementEventHandler(
      menuButton,
      () => this.handleSettingsButtonClicked(true),
      true
    );

    const menuBg = new Sprite(this.resources["menu-panel-bg"].texture);
    menuBg.name = "MENU_BG";
    menuBg.anchor.set(0.5);
    menuBg.width = consts.game.width * 0.052;
    menuBg.height = consts.game.width * 0.2197;
    menuBg.x = consts.game.width * 0.9;
    menuBg.y = consts.game.width * 0.1119;
    menuBg.visible = false;

    const closeButton = new Sprite(this.resources["button-close"].texture);
    closeButton.name = "CLOSE_BUTTON";
    closeButton.anchor.set(0.5);
    closeButton.width = 90;
    closeButton.height = 90;
    closeButton.x = 0;
    closeButton.y = -150;
    closeButton.interactive = true;
    closeButton.buttonMode = true;

    this.elementEventHandler(
      closeButton,
      () => this.handleSettingsButtonClicked(false),
      true
    );

    const soundOnButton = new Sprite(this.resources["button-sound-on"].texture);
    soundOnButton.name = "SOUND_ON_BUTTON";
    soundOnButton.anchor.set(0.5);
    soundOnButton.width = 90;
    soundOnButton.height = 90;
    soundOnButton.x = 0;
    soundOnButton.y = -45;
    soundOnButton.interactive = true;
    soundOnButton.buttonMode = true;

    this.elementEventHandler(
      soundOnButton,
      () => this.handleSoundButtonClicked(false),
      true
    );

    const soundOffButton = new Sprite(
      this.resources["button-sound-off"].texture
    );
    soundOffButton.name = "SOUND_OFF_BUTTON";
    soundOffButton.anchor.set(0.5);
    soundOffButton.width = 90;
    soundOffButton.height = 90;
    soundOffButton.x = 0;
    soundOffButton.y = -45;
    soundOffButton.interactive = true;
    soundOffButton.buttonMode = true;

    this.elementEventHandler(
      soundOffButton,
      () => this.handleSoundButtonClicked(true),
      true
    );

    const musicOnButton = new Sprite(this.resources["button-music-on"].texture);
    musicOnButton.name = "MUSIC_ON_BUTTON";
    musicOnButton.anchor.set(0.5);
    musicOnButton.width = 90;
    musicOnButton.height = 90;
    musicOnButton.x = 0;
    musicOnButton.y = 60;
    musicOnButton.interactive = true;
    musicOnButton.buttonMode = true;

    this.elementEventHandler(
      musicOnButton,
      () => this.handleMusicButtonClicked(false),
      true
    );

    const musicOffButton = new Sprite(
      this.resources["button-music-off"].texture
    );
    musicOffButton.name = "MUSIC_OFF_BUTTON";
    musicOffButton.anchor.set(0.5);
    musicOffButton.width = 90;
    musicOffButton.height = 90;
    musicOffButton.x = 0;
    musicOffButton.y = 60;
    musicOffButton.interactive = true;
    musicOffButton.buttonMode = true;

    this.elementEventHandler(
      musicOffButton,
      () => this.handleMusicButtonClicked(true),
      true
    );

    // const lobbyButton = new Sprite(this.resources["button-lobby"].texture);
    // lobbyButton.name = "LOBBY_BUTTON";
    // lobbyButton.anchor.set(0.5);
    // lobbyButton.width = 90;
    // lobbyButton.height = 90;
    // lobbyButton.x = 0;
    // lobbyButton.y = 165;
    // lobbyButton.interactive = true;
    // lobbyButton.buttonMode = true;

    // this.elementEventHandler(lobbyButton, () => this.onBackToLobby(), false);

    menuContainer.addChild(menuButton);
    menuBg.addChild(closeButton);
    menuBg.addChild(soundOffButton);
    menuBg.addChild(soundOnButton);
    menuBg.addChild(musicOffButton);
    menuBg.addChild(musicOnButton);
    //menuBg.addChild(lobbyButton);
    menuContainer.addChild(menuBg);

    uiContainer.addChild(menuContainer);
    this.addChild(uiContainer);
    this.updateBalanceText();

    this.createFortuneWheelContainer();
  }

  createFortuneWheelContainer() {
    const wheelContainer = new Container();
    wheelContainer.name = "WHEEL_CONTAINER";
    wheelContainer.visible = false;

    const fortuneWheel = new Sprite(this.resources["fortune-wheel"].texture);
    fortuneWheel.name = "FORTUNE_WHEEL";
    fortuneWheel.x = 635;
    fortuneWheel.y = 560;
    fortuneWheel.pivot.set(fortuneWheel.width / 2, fortuneWheel.height / 2);

    fortuneWheel.scale.set(0.9, 0.9);

    wheelContainer.addChild(fortuneWheel);

    const wheelFrame = new Sprite(this.resources["fortune-wheel-bg"].texture);
    wheelFrame.name = "WHEEL_FRAME";
    wheelFrame.x = 50;
    wheelFrame.y = 65;

    wheelFrame.scale.set(0.9, 0.9);

    const wheelSpin = new Sprite(this.resources["wheel-spin"].texture);
    wheelSpin.name = "WHEEL_SPIN";
    wheelSpin.x = 1425;
    wheelSpin.y = 660;
    wheelSpin.interactive = true;
    wheelSpin.buttonMode = true;

    wheelContainer.addChild(wheelSpin);
    wheelContainer.addChild(wheelFrame);

    const jackpotsContainer = new Container();
    jackpotsContainer.name = "JACKPOTS_CONTAINER";

    const jackpotsBg = new Sprite(this.resources["jackpots-bg"].texture);
    jackpotsBg.name = "JACKPOTS_BG";
    jackpotsBg.x = 1200;
    jackpotsBg.y = 50;

    const majorJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.major).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 50,
        fontWeight: 500,
        fill: ["#FDC100", "#FFFE15", "#FDC100"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#520D0D",
        strokeThickness: 3,
      }
    );
    majorJackpotAmount.name = "MAJOR_JACKPOT_AMOUNT";
    majorJackpotAmount.x = 1570;
    majorJackpotAmount.y = 320;
    majorJackpotAmount.anchor.x = 0.5;

    const minorJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.minor).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 45,
        fontWeight: 500,
        fill: ["#FDC100", "#FFFE15", "#FDC100"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#520D0D",
        strokeThickness: 3,
      }
    );
    minorJackpotAmount.name = "MINOR_JACKPOT_AMOUNT";
    minorJackpotAmount.x = 1605;
    minorJackpotAmount.y = 525;
    minorJackpotAmount.anchor.x = 0.5;

    const grandJackpotAmount = new Text(
      utils.getFormattedAmount(this.jackpots.grand).toString(),
      {
        fontFamily: "Reggae One",
        fontSize: 55,
        fontWeight: 500,
        fill: ["#FDC100", "#FFFE15", "#FDC100"],
        fillGradientStops: [0, 0.66, 1],
        align: "center",
        stroke: "#520D0D",
        strokeThickness: 3,
      }
    );
    grandJackpotAmount.name = "GRAND_JACKPOT_AMOUNT";
    grandJackpotAmount.x = 1545;
    grandJackpotAmount.y = 115;
    grandJackpotAmount.anchor.x = 0.5;

    jackpotsContainer.addChild(jackpotsBg);
    jackpotsContainer.addChild(minorJackpotAmount);
    jackpotsContainer.addChild(majorJackpotAmount);
    jackpotsContainer.addChild(grandJackpotAmount);

    wheelContainer.addChild(jackpotsContainer);

    const winLayoutContainer = new Container();
    winLayoutContainer.name = "WIN_LAYOUT_CONTAINER";
    winLayoutContainer.alpha = 0;
    winLayoutContainer.x = 960;
    winLayoutContainer.y = 540;

    const minor = new AnimatedSprite(
      this.resources["minor"].spritesheet.animations["minor"]
    );
    minor.name = "MINOR";
    minor.loop = false;
    minor.animationSpeed = 0.5;
    minor.anchor.set(0.5);
    minor.y = 0;
    minor.scale.set(1.2, 1.2);
    minor.alpha = 0;

    const major = new AnimatedSprite(
      this.resources["major"].spritesheet.animations["major"]
    );
    major.name = "MAJOR";
    major.loop = false;
    major.animationSpeed = 0.5;
    major.anchor.set(0.5);
    major.y = 0;
    major.scale.set(1.2, 1.2);
    major.alpha = 0;

    const grand = new AnimatedSprite(
      this.resources["grand"].spritesheet.animations["grand"]
    );
    grand.name = "GRAND";
    grand.loop = false;
    grand.animationSpeed = 0.5;
    grand.anchor.set(0.5);
    grand.y = 0;
    grand.scale.set(1.2, 1.2);
    grand.alpha = 0;

    const niceWin = new AnimatedSprite(
      this.resources["nice"].spritesheet.animations["nice"]
    );
    niceWin.name = "NICE";
    niceWin.loop = false;
    niceWin.animationSpeed = 0.5;
    niceWin.anchor.set(0.5);
    niceWin.y = 0;
    niceWin.scale.set(1.25, 1.25);
    niceWin.alpha = 0;

    const superWin = new AnimatedSprite(
      this.resources["super"].spritesheet.animations["super"]
    );
    superWin.name = "SUPER";
    superWin.loop = false;
    superWin.animationSpeed = 0.5;
    superWin.anchor.set(0.5);
    superWin.y = 0;
    superWin.scale.set(1.2, 1.2);
    superWin.alpha = 0;

    const sensationalWin = new AnimatedSprite(
      this.resources["sensational"].spritesheet.animations["sensational"]
    );
    sensationalWin.name = "SENSATIONAL";
    sensationalWin.loop = false;
    sensationalWin.animationSpeed = 0.5;
    sensationalWin.anchor.set(0.5);
    sensationalWin.y = 0;
    sensationalWin.scale.set(1.25, 1.25);
    sensationalWin.alpha = 0;

    winLayoutContainer.addChild(minor);
    winLayoutContainer.addChild(major);
    winLayoutContainer.addChild(grand);
    winLayoutContainer.addChild(niceWin);
    winLayoutContainer.addChild(superWin);
    winLayoutContainer.addChild(sensationalWin);

    const winAmountText = new Text("", {
      fontFamily: "Reggae One",
      fontSize: 90,
      fill: ["#FDC100", "#FFFE15", "#FDC100"],
      fillGradientStops: [0, 0.66, 1],
      align: "center",
      stroke: "#520D0D",
      strokeThickness: 3,
    });
    winAmountText.name = "WIN_AMOUNT_TEXT";
    winAmountText.anchor.set(0.5);
    winAmountText.y += 80;
    winAmountText.texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;
    winLayoutContainer.addChild(winAmountText);

    wheelContainer.addChild(winLayoutContainer);

    wheelContainer.pivot.y = 560;
    wheelContainer.pivot.x = 950;
    wheelContainer.x = consts.game.width / 2;
    wheelContainer.y = consts.game.height / 2;
    const wheelContainerRatio = Math.min(
      consts.game.width / 1920,
      consts.game.height / 1080
    );
    wheelContainer.scale.set(wheelContainerRatio, wheelContainerRatio);

    this.addChild(wheelContainer);

    const curtainContainer = new Container();
    curtainContainer.name = "CURTAIN_CONTAINER";
    curtainContainer.width = consts.game.width;
    curtainContainer.height = consts.game.height;
    curtainContainer.alpha = 0;
    curtainContainer.visible = false;

    const curtainBg = new Sprite(this.resources["curtain-bg"].texture);
    curtainBg.name = "CURTAIN_BG";
    curtainBg.width = consts.game.width;
    curtainBg.height = consts.game.height;

    const curtainLeftPiece = new Sprite(
      this.resources["curtain-piece"].texture
    );
    curtainLeftPiece.name = "CURTAIN_LEFT_PIECE";
    curtainLeftPiece.x = 0;
    curtainLeftPiece.width = consts.game.width / 2 + 36;
    curtainLeftPiece.height = consts.game.height;

    const curtainRightPiece = new Sprite(
      this.resources["curtain-piece"].texture
    );
    curtainRightPiece.name = "CURTAIN_RIGHT_PIECE";
    curtainRightPiece.x = consts.game.width;
    curtainRightPiece.width = consts.game.width / 2 + 36;
    curtainRightPiece.height = consts.game.height;
    curtainRightPiece.scale.x *= -1;

    curtainContainer.addChild(curtainLeftPiece);
    curtainContainer.addChild(curtainRightPiece);
    curtainContainer.addChild(curtainBg);
    this.addChild(curtainContainer);

    // const freeSpinContainer = new Container()
    // freeSpinContainer.name = "FREE_SPIN_CONTAINER"
    // freeSpinContainer.x = consts.game.width * 0.1666
    // freeSpinContainer.y = consts.game.height * 0.1018
    // freeSpinContainer.visible = false

    // const freeSpinModal = new AnimatedSprite(this.resources["freespin"].spritesheet.animations["freespin"]);
    // freeSpinModal.name = "FREE_SPIN_MODAL";
    // freeSpinModal.loop = false;
    // freeSpinModal.animationSpeed = 0.5
    // freeSpinModal.x = 60
    // freeSpinModal.y = 300
    // freeSpinModal.scale.set(1.2, 1.2)
    // freeSpinModal.alpha = 0

    // const freeSpin = new Sprite(this.resources[`${this.locale}-freespin`].texture);
    // freeSpin.name = "FREE_SPIN"
    // freeSpin.x = 0
    // freeSpin.y = 0;
    // freeSpin.alpha = 0

    // const modalText = new Text("", {
    //   fontFamily: "Reggae One",
    //   fontSize: 80,
    //   fill: ["#d4af37", "#fcc200", "#fede00", "#ffdf00", "#fcc200", "#d4af37"],
    //   fillGradientStops: [0, 0.35, 0.38, 0.54, 0.61, 0.98],
    //   align: "center",
    //   stroke: "#6c3207",
    //   strokeThickness: 4,
    //   dropShadow: true,
    //   dropShadowBlur: 0,
    //   dropShadowDistance: 2,
    // });
    // modalText.name = "MODAL_TEXT"
    // modalText.anchor.set(0.5)
    // modalText.x = 640
    // modalText.y = 460
    // modalText.alpha = 1

    // const spinCountText = new Text("", {
    //   fontFamily: "Reggae One",
    //   fontSize: 80,
    //   fill: ["#d4af37", "#fcc200", "#fede00", "#ffdf00", "#fcc200", "#d4af37"],
    //   fillGradientStops: [0, 0.35, 0.38, 0.54, 0.61, 0.98],
    //   align: "center",
    //   stroke: "#6c3207",
    //   strokeThickness: 4,
    //   dropShadow: true,
    //   dropShadowBlur: 0,
    //   dropShadowDistance: 2,
    // });
    // spinCountText.name = "SPIN_COUNT_TEXT"
    // spinCountText.anchor.set(0.5)
    // spinCountText.x = 325
    // spinCountText.y = 615
    // spinCountText.alpha = 1

    // freeSpinContainer.addChild(freeSpinModal)
    // freeSpinContainer.addChild(freeSpin)
    // freeSpinContainer.addChild(modalText)
    // freeSpinContainer.addChild(spinCountText)

    // const containerRatio = Math.min(window.innerWidth / 1920)

    // fogAnimationContainer.scale.set(containerRatio, containerRatio);
    // freeSpinContainer.scale.set(containerRatio, containerRatio)

    // this.addChild(fogAnimationContainer)
    // this.addChild(freeSpinContainer)

    this.addUiEventListeners();
  }

  elementEventHandler(element, firingFunction, bypassSpinControl) {
    const elementScale = element.scale.x;
    element.on("click", () => {
      if (!this.isSpinAvailable && !bypassSpinControl) return false;
      firingFunction();
    });
    element.on("tap", () => {
      if (!this.isSpinAvailable && !bypassSpinControl) return false;
      this.sounds["ui"].play();
      firingFunction();
    });
    element.on("mouseover", () => {
      const scaleAmount = elementScale + elementScale * 0.1;
      if (this.isSpinAvailable || bypassSpinControl) {
        element.scale.set(scaleAmount, scaleAmount);
        this.sounds["ui"].play();
      }
    });
    element.on("mouseout", () => {
      element.scale.set(elementScale, elementScale);
    });
  }

  addUiEventListeners() {
    const gameContext = this;
    this.menuController = new Vue({
      el: "#modals",
      watch: {
        gameMode: function (newValue) {
          this.settings.gameMode = newValue;
        }.bind(this),
        autoPlayCount: function (newValue) {
          this.settings.autoPlayCount = newValue;
        }.bind(this),
        spinMode: function (newValue) {
          this.settings.spinMode = newValue;
        }.bind(this),
        betAmount: function (newValue) {
          this.settings.betAmount = newValue;
          this.setBuyBonus(newValue);
        }.bind(this),
        showMenuModal: function (newValue) {},
      },
      data: {
        showAlertModal: false,
        alertModalText: "",
        showBuyBonusModal: false,
        showEnterBonusModal: false,
        showBonusResultModal: false,
        showBackModal: false,
        isBuyChipClicked: false,
        buyBonusAmount: utils.getShortFormat(
          this.settings.betAmount *
            this.settings.chosenLines *
            this.variationConfig.bonus.multiplier
        ),
        showMenuModal: false,
        currentTab: 1,
        symbols: this.variationConfig.symbols.splice(0, 13),
        winLines: this.variationConfig.lines,
        allowedBets: this.variationConfig.allowedBets,
        betAmount: this.variationConfig.allowedBets[0],
        chosenLines: this.settings.chosenLines,
        gameMode: this.settings.gameMode,
        spinMode: this.settings.spinMode,
        loggedInUser: this.user,
        autoPlayCount: 0,
        freeSpinCount: 0,
        scattersCount: 0,
        bonusTotalWin: 0,
      },
      computed: {
        getUser() {
          return this.loggedInUser;
        },
        getLangFile() {
          let params = new URLSearchParams(window.location.search);
          if (params.get("lang_local") && params.get("lang_local") !== "ru") {
            return lang[params.get("lang_local")];
          } else {
            return lang["en"];
          }
        },
      },
      methods: {
        handleBonusModal(bool, data) {
          if (bool === true) {
            this.freeSpinCount = data.freeSpins.length;
          }
          this.showEnterBonusModal = bool;
        },
        handleBonusResultModal(bool) {
          this.bonusTotalWin = utils.getFormattedAmount(
            gameContext.currentBonusTotalWin
          );
          this.showBonusResultModal = bool;
        },
        changeBackModalVisibility(bool, isBuyChipClicked) {
          this.showBackModal = bool;
          this.isBuyChipClicked = isBuyChipClicked;
        },
        handleBackModal() {
          this.showBackModal = false;
          if (this.isBuyChipClicked) {
            window.parent.postMessage("openBuyChipsModalFafafa", "*");
          } else {
            window.parent.postMessage("closeSlotsFafafa", "*");
          }
        },
        goLeft() {
          if (this.currentTab == 1) {
            this.currentTab = 4;
          } else {
            this.currentTab--;
          }
        },
        goRight() {
          if (this.currentTab == 4) {
            this.currentTab = 1;
          } else {
            this.currentTab++;
          }
        },
        buyBonus() {
          this.showBuyBonusModal = false;
          gameContext.buyBonusGame();
        },
        setAlertModalVisibility(bool) {
          this.showAlertModal = bool;
        },
        setAlertModalText(text) {
          this.alertModalText = text;
        },
        changeMenuVisibility(bool = true) {
          this.showMenuModal = bool;
        },
        changeBuyBonusModalVisibility(bool = true) {
          this.showBuyBonusModal = bool;
        },
      },
    });
  }

  setBalance(amount) {
    let num = Number(amount);
    this.user.chips = num;
    this.updateBalanceText(num);
  }

  setJackpots(amount) {
    const slotBoard = this.getChildByName("SLOT_BOARD");
    const jackpotContainer = slotBoard.getChildByName("JACKPOT_CONTAINER");
    const minor = jackpotContainer.getChildByName("MINOR_JACKPOT_AMOUNT");
    const major = jackpotContainer.getChildByName("MAJOR_JACKPOT_AMOUNT");
    const grand = jackpotContainer.getChildByName("GRAND_JACKPOT_AMOUNT");
    const wheelJackpotsContainer =
      this.getChildByName("WHEEL_CONTAINER").getChildByName(
        "JACKPOTS_CONTAINER"
      );
    const wheelMinor = wheelJackpotsContainer.getChildByName(
      "MINOR_JACKPOT_AMOUNT"
    );
    const wheelMajor = wheelJackpotsContainer.getChildByName(
      "MAJOR_JACKPOT_AMOUNT"
    );
    const wheelGrand = wheelJackpotsContainer.getChildByName(
      "GRAND_JACKPOT_AMOUNT"
    );
    minor.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.minor
      )
      .toString();
    major.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.major
      )
      .toString();
    grand.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.grand
      )
      .toString();
    wheelMinor.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.minor
      )
      .toString();
    wheelMajor.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.major
      )
      .toString();
    wheelGrand.text = utils
      .getFormattedAmount(
        amount *
          this.settings.chosenLines *
          this.variationConfig.jackpotMultipliers.grand
      )
      .toString();
  }

  setBuyBonus(amount) {
    const buyBonusContainer = this.getChildByName("BUY_BONUS_CONTAINER");
    const buyBonusButton = buyBonusContainer.getChildByName("BUY_BONUS_BUTTON");
    const buyBonusButtonAmount =
      buyBonusButton.getChildByName("BUY_BONUS_AMOUNT");

    buyBonusButtonAmount.text = utils.getShortFormat(
      amount * this.settings.chosenLines * this.variationConfig.bonus.multiplier
    );
    this.menuController.buyBonusAmount = utils.getShortFormat(
      amount * this.settings.chosenLines * this.variationConfig.bonus.multiplier
    );
  }

  onStopButtonClicked() {
    this._isAutoPlayRunning = false;
    this.settings.autoPlayCount = 0;
    this.settings.gameMode = consts.game.gameMode.DEFAULT;
    this.setStopButtonText("");
    this.setStopButtonVisibility(false);
  }

  async onSpinButtonClicked() {
    if (!this.isSpinAvailable) return;
    this.isSpinAvailable = false;
    await this.spin(this.settings.autoPlayCount);
  }

  onFastButtonClicked() {
    const fastButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("FAST_BUTTON");
    if (this.settings.spinMode === "normal") {
      this.settings.spinMode = "quick";
      this.selectedSpinMode = "quick";
      fastButton.texture = this.resources["button-fast-selected"].texture;
    } else {
      this.settings.spinMode = "normal";
      this.selectedSpinMode = "normal";
      fastButton.texture = this.resources["button-fast"].texture;
    }
  }

  onMaxbetButtonClicked() {
    const maxbetButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_LEFT_CONTAINER")
      .getChildByName("MAXBET_BUTTON");

    const allowedBets = this.variationConfig.allowedBets;
    if (
      allowedBets.indexOf(this.settings.betAmount) !==
      allowedBets.length - 1
    ) {
      this.settings.betAmount = allowedBets[allowedBets.length - 1];
      maxbetButton.texture = this.resources["button-maxbet-selected"].texture;
      this.updateBetAmountText(this.settings.betAmount);
      this.setJackpots(this.settings.betAmount);
      this.setBuyBonus(this.settings.betAmount);
    } else {
      this.settings.betAmount = allowedBets[0];
      maxbetButton.texture = this.resources["button-maxbet"].texture;
      this.updateBetAmountText(this.settings.betAmount);
      this.setJackpots(this.settings.betAmount);
      this.setBuyBonus(this.settings.betAmount);
    }
  }

  updateBetAmountText(amount) {
    const betAmountText = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_LEFT_CONTAINER")
      .getChildByName("BET_TEXT");

    betAmountText.text = utils
      .getShortFormat(amount * this.settings.chosenLines)
      .toString();
  }

  changeBetAmount(to = 1) {
    const allowedBets = this.variationConfig.allowedBets;
    const currentIndex = allowedBets.indexOf(this.settings.betAmount);
    const maxbetButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_LEFT_CONTAINER")
      .getChildByName("MAXBET_BUTTON");

    if (typeof allowedBets[currentIndex + to] === "undefined") {
      if (to < 0) {
        this.settings.betAmount = allowedBets[allowedBets.length - 1];
      } else {
        this.settings.betAmount = allowedBets[0];
      }
    } else {
      this.settings.betAmount = allowedBets[currentIndex + to];
    }

    if (this.settings.betAmount === allowedBets[allowedBets.length - 1]) {
      maxbetButton.texture = this.resources["button-maxbet-selected"].texture;
    } else if (currentIndex === allowedBets.length - 1) {
      maxbetButton.texture = this.resources["button-maxbet"].texture;
    }
    this.updateBetAmountText(this.settings.betAmount);
    this.setJackpots(this.settings.betAmount);
    this.setBuyBonus(this.settings.betAmount);
  }

  onInfoButtonClicked() {
    this.onMenuButtonClicked();
  }

  onLongTapSpinButton() {
    this.autoButtonsShowing = true;
    const autoContainer = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("AUTO_BUTTONS_CONTAINER");
    autoContainer.visible = true;
  }

  onAutoButtonClicked(count) {
    this.autoButtonsShowing = false;
    const autoContainer = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("AUTO_BUTTONS_CONTAINER");
    const button = autoContainer.getChildByName(`AUTO_${count}`);
    button.texture =
      this.resources[`button-auto-spin-${count}-selected`].texture;
    setTimeout(() => {
      autoContainer.visible = false;
      this.settings.autoPlayCount = count;
      this.onSpinButtonClicked();
      button.texture = this.resources[`button-auto-spin-${count}`].texture;
    }, 300);
  }

  handleSettingsButtonClicked(bool) {
    const menu = this.getChildByName("UI_CONTAINER")
      .getChildByName("MENU_CONTAINER")
      .getChildByName("MENU_BG");
    menu.visible = bool;
  }

  handleSoundButtonClicked(bool) {
    const soundButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("MENU_CONTAINER")
      .getChildByName("MENU_BG")
      .getChildByName("SOUND_ON_BUTTON");
    soundButton.visible = bool;
    Object.keys(this.sounds).map((key, index) => {
      this.sounds[key].mute(!bool);
    });
    this.settings.mute = !this.settings.mute;
  }

  handleMusicButtonClicked(bool) {
    const musicButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("MENU_CONTAINER")
      .getChildByName("MENU_BG")
      .getChildByName("MUSIC_ON_BUTTON");
    musicButton.visible = bool;
    this.sounds["background"].mute(!bool);
    this.sounds["background_slow"].mute(!bool);
  }

  closeAutoContainer() {
    this.autoButtonsShowing = false;
    const autoContainer = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("AUTO_BUTTONS_CONTAINER");
    autoContainer.visible = false;
  }

  onBackToLobby() {
    this.closeAutoContainer();
    this.handleSettingsButtonClicked(false);
    this.menuController.changeBackModalVisibility(true, false);
  }

  onBuyChipClicked() {
    this.closeAutoContainer();
    this.menuController.changeBackModalVisibility(true, true);
  }

  async spin(autoPlayCount = 0) {
    if (this.user.chips < this.settings.betAmount * this.settings.chosenLines) {
      this._isAutoPlayRunning = false;
      this.setStopButtonVisibility(false);
      if (autoPlayCount <= 100) this.setStopButtonText("");
      this.isSpinAvailable = true;

      this.menuController.setAlertModalVisibility(true);
      this.menuController.setAlertModalText("Insufficient balance.");
      return false;
    }

    const panelWinAmount = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("WIN_AMOUNT_TEXT");
    panelWinAmount.text = "GOOD LUCK!";

    if (autoPlayCount > 0) {
      this._isAutoPlayRunning = true;
      this.setStopButtonVisibility(true);
      this.setStopButtonText(autoPlayCount.toString());
    }

    const spinResponse = await auth.spin({
      chosenLines: consts.game.chosenLines,
      betAmount: this.settings.betAmount,
    });

    const spinResult = spinResponse.data;

    logEvent(analytics, "SLOT_SPIN_PANDA", {
      platform: utils.isMobile ? "MOBILE" : "WEB",
      params: {
        betAmount: this.settings.betAmount,
        chosenLines: this.settings.chosenLines,
      },
    });

    if (typeof spinResult === "undefined") {
      this._isAutoPlayRunning = false;
      this.setStopButtonVisibility(false);
      if (autoPlayCount <= 100) this.setStopButtonText("");
      this.isSpinAvailable = true;

      this.menuController.setAlertModalVisibility(true);
      this.menuController.setAlertModalText("Internet Connection Problem");
      return false;
    }

    this.setBalance(
      this.user.chips - this.settings.betAmount * this.settings.chosenLines
    );

    const calculatedSpinResult = this.calculateSpinResult(
      spinResult.results,
      "normal"
    );

    if (this.oncePlayed) {
      for (let i = 0; i < 5; i++) {
        const reel = this.getChildByName("SLOT_BOARD")
          .getChildByName("REELS_CONTAINER")
          .getChildByName(`REEL_${i}`);
        for (let j = 0; j < 3; j++) {
          reel.removeChildAt(0);
        }
      }
    }
    this.oncePlayed = true;

    await this.animateSpin(calculatedSpinResult);

    this.setBalance(this.user.chips + calculatedSpinResult.winAmount);

    if (calculatedSpinResult.bonuses.length > 0) {
      await this.animateCurtain(calculatedSpinResult, "enterWheel");
      await this.handleWheelSpin();
      await this.spinWheel(calculatedSpinResult);
    }

    // if(calculatedSpinResult.scatters.freeSpins) {
    //   await this.enterBonusMode(calculatedSpinResult.scatters)
    //   await this.playBonusMode(calculatedSpinResult.scatters.freeSpins, false)
    //   await this.exitBonusMode()
    // }

    this.user = spinResult.user;

    this.setBalance(spinResult.user.chips);

    if (this.settings.autoPlayCount > 0) {
      autoPlayCount -= 1;
      this.settings.autoPlayCount = autoPlayCount;
      if (this._isAutoPlayRunning) await this.spin(autoPlayCount);
      if (autoPlayCount === 0) {
        this.setStopButtonVisibility(false);
        this.setStopButtonText("");
        this._isAutoPlayRunning = false;
      }
    }

    this.isSpinAvailable = true;
  }

  async handleWheelSpin() {
    return await new Promise((resolve) => {
      const spinWheelButton =
        this.getChildByName("WHEEL_CONTAINER").getChildByName("WHEEL_SPIN");
      const elementScale = spinWheelButton.scale.x;
      spinWheelButton.on("click", () => {
        resolve(true);
        spinWheelButton.scale.set(elementScale, elementScale);
      });
      spinWheelButton.on("tap", () => {
        resolve(true);
        spinWheelButton.scale.set(elementScale, elementScale);
      });
      spinWheelButton.on("mouseover", () => {
        const scaleAmount = elementScale + elementScale * 0.1;
        spinWheelButton.scale.set(scaleAmount, scaleAmount);
        this.sounds["ui"].play();
      });
      spinWheelButton.on("mouseout", () => {
        spinWheelButton.scale.set(elementScale, elementScale);
      });
    });
  }

  async enterBonusMode(resultScatters) {
    document.body.style.backgroundImage =
      'url("./assets/bonus-background.jpg")';
    const slotBoard = this.getChildByName("SLOT_BOARD");
    const symbolFrameBg = slotBoard.getChildByName("SYMBOL_FRAME_BG");
    symbolFrameBg.texture = this.resources["bonusFrame"].texture;

    const buyBonus = this.getChildByName("BUY_BONUS_CONTAINER");

    buyBonus.visible = false;

    const controlPanelRight = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER");

    const fastButton = controlPanelRight.getChildByName("FAST_BUTTON");
    const spinButton = controlPanelRight.getChildByName("SPIN_BUTTON");
    const stopButton = controlPanelRight.getChildByName("STOP_BUTTON");
    const spinBg = controlPanelRight.getChildByName("SPIN_BG");

    this.currentFreeSpinCount = resultScatters.freeSpins.length;
    this.startingFreeSpinCount = resultScatters.freeSpins.length;
    spinBg.getChildByName("REMAINING_SPIN_TEXT").text =
      "Free Spins " +
      this.currentFreeSpinCount +
      " / " +
      this.startingFreeSpinCount;

    spinBg.visible = true;
    fastButton.alpha = 0.6;

    spinButton.visible = false;
    stopButton.visible = false;

    const bonusWinText = new Text("", {
      fontFamily: "Reggae One",
      fontSize: 6 * 15,
      fill: ["#FDC100", "#FFFE15", "#FDC100"],
      fillGradientStops: [0, 0.66, 1],
      align: "center",
      stroke: "#520D0D",
      strokeThickness: 3,
    });
    bonusWinText.name = "BONUS_WIN_AMOUNT_TEXT";
    bonusWinText.anchor.set(0.5);
    bonusWinText.texture.baseTexture.scaleMode = SCALE_MODES.LINEAR;
    bonusWinText.visible = false;

    slotBoard.addChild(bonusWinText);
    const panelWinAmount = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("WIN_AMOUNT_TEXT");
    panelWinAmount.text = "GOOD LUCK!";
    this.gameType = "bonus";
    this.settings.spinMode = "normal";
    await this.showBonusModal(resultScatters);
    this.sounds["background_slow"].fade(0.3, 0, 1000);
    setTimeout(() => {
      this.sounds["background_slow"].stop();
    }, 1000);
    this.sounds["background"].loop(true);
    this.sounds["background"].play();
    this.sounds["background"].fade(0, 0.3, 1000);
  }

  async exitBonusMode() {
    const slotBoard = this.getChildByName("SLOT_BOARD");
    const symbolFrameBg = slotBoard.getChildByName("SYMBOL_FRAME_BG");

    document.body.style.backgroundImage = 'url("./assets/background.jpg")';
    symbolFrameBg.texture = this.resources["symbolFrameBg"].texture;
    slotBoard.getChildByName("BONUS_WIN_AMOUNT_TEXT").destroy();

    const buyBonus = this.getChildByName("BUY_BONUS_CONTAINER");

    buyBonus.visible = true;

    const panelWinAmount = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("WIN_AMOUNT_TEXT");
    panelWinAmount.text = "GOOD LUCK!";
    const controlPanelRight = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER");

    const fastButton = controlPanelRight.getChildByName("FAST_BUTTON");
    const spinButton = controlPanelRight.getChildByName("SPIN_BUTTON");
    const stopButton = controlPanelRight.getChildByName("STOP_BUTTON");
    const spinBg = controlPanelRight.getChildByName("SPIN_BG");

    spinBg.visible = false;
    fastButton.alpha = 1;

    await this.showBonusResultModal();

    this.currentFreeSpinCount = 0;
    this.startingFreeSpinCount = 0;

    if (this.autoPlayCount > 0) {
      stopButton.visible = true;
    } else {
      spinButton.visible = true;
    }

    this.currentBonusTotalWin = 0;
    this.gameType = "normal";
    this.settings.spinMode = this.selectedSpinMode;
    this.sounds["background"].fade(0.3, 0, 1000);
    setTimeout(() => {
      this.sounds["background"].stop();
    }, 1000);
    this.sounds["background_slow"].play();
    this.sounds["background_slow"].fade(0, 0.3, 1000);
  }

  changeReels(reelType) {
    const fakeReels =
      reelType === "normal"
        ? this.variationConfig.reels[0]
        : this.variationConfig.reels[1];
    let fakeSymbols = [];
    for (let i = 0; i < 5; i++) {
      fakeSymbols.push(_.shuffle(fakeReels[i]).slice(0, 3));
    }
    for (let i = 0; i < 5; i++) {
      const reel = this.getChildByName("SLOT_BOARD")
        .getChildByName("REELS_CONTAINER")
        .getChildByName(`REEL_${i}`);
      reel.removeChildren(0, 3);

      let lastIndex = 0;
      fakeSymbols[i].forEach((symbolData, symbolIndex) => {
        let symbol = new Symbol(this.resources, {
          num: symbolData,
          index: symbolIndex,
          fakeSymbol: true,
        });

        const symbolSprite = symbol.sprite;

        symbolSprite.x = 0;
        symbolSprite.y = symbolIndex * 252;

        switch (symbol.spriteName) {
          case "nine":
            symbolSprite.x += 80;
            symbolSprite.y += 59;
            break;
          case "ten":
            symbolSprite.x += 55;
            symbolSprite.y += 48;
            break;
          case "jack":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "queen":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "king":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "ace":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "orchid":
            symbolSprite.x += 30;
            symbolSprite.y += 42;
            break;
          case "vase":
            symbolSprite.x += 30;
            symbolSprite.y += 35;
            break;
          case "knife":
            symbolSprite.x += 30;
            symbolSprite.y += 20;
            break;
          case "horse":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "chengiz":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "wild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "scatter":
            symbolSprite.x += 25;
            symbolSprite.y += 32;
            break;
          case "expandedWild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
        }

        symbolSprite.y -= 252 * 3;

        reel.addChildAt(symbolSprite, lastIndex);
        lastIndex++;
      });
    }
  }

  async showBonusModal(resultScatters) {
    return new Promise((resolve) => {
      this.menuController.handleBonusModal(true, resultScatters);
      const timer = setTimeout(() => {
        this.menuController.handleBonusModal(false, null);
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        resolve(true);
        clearTimeout(timer);
      }, 10000);
      const clickHandler = () => {
        this.menuController.handleBonusModal(false, null);
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        clearTimeout(timer);
        resolve(true);
      };
      document.body.addEventListener("click", clickHandler.bind(this));
      document.body.addEventListener("keyup", clickHandler.bind(this));
    });
  }

  async showBonusResultModal() {
    return new Promise((resolve) => {
      this.menuController.handleBonusResultModal(true);
      const timer = setTimeout(() => {
        this.menuController.handleBonusResultModal(false);
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        resolve(true);
        clearTimeout(timer);
      }, 10000);
      const clickHandler = () => {
        this.menuController.handleBonusResultModal(false);
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        clearTimeout(timer);
        resolve(true);
      };
      document.body.addEventListener("click", clickHandler.bind(this));
      document.body.addEventListener("keyup", clickHandler.bind(this));
    });
  }

  async buyBonusGame() {
    if (
      this.user.chips <
      this.settings.betAmount *
        this.settings.chosenLines *
        this.variationConfig.bonus.multiplier
    ) {
      this.menuController.setAlertModalVisibility(true);
      this.menuController.setAlertModalText("Insufficient balance.");
      return false;
    }

    this.isSpinAvailable = false;

    const panelWinAmount = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("WIN_AMOUNT_TEXT");
    panelWinAmount.text = "GOOD LUCK!";

    const spinResponse = await auth.spin({
      chosenLines: consts.game.chosenLines,
      betAmount: this.settings.betAmount,
      buyBonus: true,
    });

    const spinResult = spinResponse.data;

    logEvent(analytics, "SLOT_BUY_BONUS_PANDA", {
      platform: utils.isMobile ? "MOBILE" : "WEB",
      params: {
        price:
          this.settings.betAmount *
          this.settings.chosenLines *
          this.variationConfig.bonus.multiplier,
      },
    });

    if (this.oncePlayed) {
      for (let i = 0; i < 5; i++) {
        const reel = this.getChildByName("SLOT_BOARD")
          .getChildByName("REELS_CONTAINER")
          .getChildByName(`REEL_${i}`);
        for (let j = 0; j < 3; j++) {
          reel.removeChildAt(0);
        }
      }
    }

    this.oncePlayed = true;

    const calculatedSpinResult = this.calculateSpinResult(
      spinResult.results,
      "normal"
    );

    console.log("calculated result", calculatedSpinResult);

    this.setBalance(
      this.user.chips -
        this.settings.betAmount *
          this.settings.chosenLines *
          this.variationConfig.bonus.multiplier
    );

    await this.animateSpin(calculatedSpinResult);

    if (calculatedSpinResult.bonuses.length > 0) {
      await this.animateCurtain(calculatedSpinResult, "enterWheel");
      await this.handleWheelSpin();
      await this.spinWheel(calculatedSpinResult);
    }
    // if(calculatedSpinResult.scatters.freeSpins) {
    //   await this.enterBonusMode(calculatedSpinResult.scatters)
    //   await this.playBonusMode(calculatedSpinResult.scatters.freeSpins, false)
    //   await this.exitBonusMode()
    // }

    this.user = spinResult.user;
    this.setBalance(spinResult.user.chips);

    this.isSpinAvailable = true;
  }

  async animateCurtain(result, type) {
    return await new Promise((resolve) => {
      const curtainContainer = this.getChildByName("CURTAIN_CONTAINER");
      const curtainLeft = curtainContainer.getChildByName("CURTAIN_LEFT_PIECE");
      const curtainRight = curtainContainer.getChildByName(
        "CURTAIN_RIGHT_PIECE"
      );

      let tl = gsap.timeline({
        onReverseComplete: () => {
          curtainContainer.visible = false;
          resolve(true);
        },
      });
      curtainContainer.visible = true;
      tl.fromTo(
        curtainContainer,
        {
          pixi: {
            alpha: 0,
          },
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.5,
          ease: "none",
        }
      );
      tl.fromTo(
        curtainLeft,
        {
          pixi: {
            x: -(window.innerWidth / 2),
          },
        },
        {
          pixi: {
            x: 0,
          },
          duration: 2,
          ease: "none",
        },
        0.5
      );
      tl.fromTo(
        curtainRight,
        {
          pixi: {
            x: window.innerWidth + window.innerWidth / 2,
          },
        },
        {
          pixi: {
            x: window.innerWidth,
          },
          duration: 2,
          ease: "none",
          onComplete: async () => {
            const slotBoard = this.getChildByName("SLOT_BOARD");
            const uiContainer = this.getChildByName("UI_CONTAINER");
            const buyBonusContainer = this.getChildByName(
              "BUY_BONUS_CONTAINER"
            );
            const wheelContainer = this.getChildByName("WHEEL_CONTAINER");
            switch (type) {
              case "enterWheel":
                document.body.style.backgroundImage =
                  'url("./assets/wheel-background.jpg")';
                slotBoard.visible = false;
                uiContainer.visible = false;
                buyBonusContainer.visible = false;
                wheelContainer.visible = true;
                break;
              case "exitWheel":
                document.body.style.backgroundImage =
                  'url("./assets/background.jpg")';
                slotBoard.visible = true;
                uiContainer.visible = true;
                buyBonusContainer.visible = true;
                wheelContainer.visible = false;
                break;
              case "enterBonus":
                slotBoard.visible = true;
                uiContainer.visible = true;
                buyBonusContainer.visible = true;
                wheelContainer.visible = false;
                await this.enterBonusMode(result.scatters);
                break;
              case "exitBonus":
                await this.exitBonusMode();
                break;
            }
            setTimeout(() => {
              tl.reverse();
            }, 1000);
          },
        },
        0.5
      );
    });
  }

  async spinWheel(result) {
    return await new Promise((resolve) => {
      const prizes = [
        "25X",
        "+20 Free Spins",
        "50X",
        "MAJOR JACKPOT",
        "+15 Free Spins",
        "75X",
        "+12 Free Spins",
        "MINOR JACKPOT",
        "+10 Free Spins",
        "100X",
        "+7 Free Spins",
        "GRAND JACKPOT",
      ];
      const wheel =
        this.getChildByName("WHEEL_CONTAINER").getChildByName("FORTUNE_WHEEL");
      const numberOfSectors = 12;
      const piTwo = Math.PI * 2;
      const radiansPerSector = piTwo / numberOfSectors;
      const prize = result.bonuses[0];
      const choosenSector = prizes.indexOf(prize.name) + 1;
      const sectorsToMinus = numberOfSectors - choosenSector;
      const rotation = sectorsToMinus * radiansPerSector;
      wheel.rotation = 0;
      const totalRotation = piTwo * 4 + rotation;
      this.playSound("wheel-start");
      this.sounds["wheel-loop"].play();
      let integer = 0;
      gsap.to(wheel, {
        rotation: `+=${totalRotation}`,
        ease: CustomEase.create(
          "custom",
          "M0,0,C0.126,0.382,0.114,0.475,0.234,0.662,0.31,0.78,0.402,0.87,0.496,0.918,0.594,0.968,0.702,0.994,0.8,1,0.984,1.01,0.945,1,1,1"
        ),
        duration: 8,
        onUpdate: () => {
          if (
            parseInt(wheel.rotation) === integer &&
            parseInt(wheel.rotation) % parseInt(piTwo) === 0
          ) {
            this.sounds["wheel-loop"].play();
            integer += parseInt(piTwo);
          }
        },
        onComplete: async () => {
          this.sounds["wheel-loop"].play();
          switch (prize.type) {
            case "freeSpin":
              await this.animateCurtain(result, "enterBonus");
              await this.playBonusMode(result.scatters.freeSpins, false);
              await this.animateCurtain({}, "exitBonus");
              resolve(true);
              break;
            case "multiplier":
              await this.handleWheelPopups(prize.type, prize.amount);
              await this.animateCurtain({}, "exitWheel");
              resolve(true);
              break;
            case "jackpot":
              await this.handleWheelPopups(prize.name, prize.amount);
              await this.animateCurtain({}, "exitWheel");
              resolve(true);
              break;
          }
        },
      });
    });
  }

  async playBonusMode(freeSpins, inBonusMode) {
    let playedBefore = true;

    // if (!inBonusMode) {
    //   playedBefore = false
    // }

    const remainingSpinText = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("SPIN_BG")
      .getChildByName("REMAINING_SPIN_TEXT");

    for (const item of freeSpins) {
      if (playedBefore) {
        for (let i = 0; i < 5; i++) {
          const reel = this.getChildByName("SLOT_BOARD")
            .getChildByName("REELS_CONTAINER")
            .getChildByName(`REEL_${i}`);
          for (let j = 0; j < 3; j++) {
            reel.removeChildAt(0);
          }
        }
      }
      playedBefore = true;
      const result = this.calculateSpinResult(item, "bonus");
      this.currentFreeSpinCount--;
      remainingSpinText.text =
        "Free Spins " +
        this.currentFreeSpinCount +
        " / " +
        this.startingFreeSpinCount;
      await this.animateSpin(result);
      this.currentBonusTotalWin = this.currentBonusTotalWin + result.winAmount;
      if (item.wins.freeSpins) {
        this.currentFreeSpinCount =
          this.currentFreeSpinCount + item.wins.freeSpins.length;
        this.startingFreeSpinCount =
          this.startingFreeSpinCount + item.wins.freeSpins.length;
        remainingSpinText.text =
          "Free Spins " +
          this.currentFreeSpinCount +
          " / " +
          this.startingFreeSpinCount;
        this.menuController.freeSpinCount =
          this.menuController.freeSpinCount + item.wins.freeSpins.length;
        await this.playBonusMode(item.wins.freeSpins, true);
      }
    }
  }

  calculateAnimationDetails(result) {
    let scatterPositions = [];
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 3; j++) {
        if (result.board[i][j].num === 12) {
          scatterPositions.push(i);
        }
      }
    }
    let scatterChanceReels = [false, false, false, false, false];
    if (scatterPositions.length >= 2) {
      for (let i = 0; i < 5; i++) {
        if (i >= scatterPositions[1]) {
          if (typeof scatterChanceReels[i + 1] !== "undefined") {
            scatterChanceReels[i + 1] = true;
          }
        }
      }
    }
    let reelAnimationsDetails = [];
    for (let i = 0; i < 5; i++) {
      const normalDuration =
        (consts.game.spinModeDuration.NORMAL +
          i * (consts.game.spinModeDuration.NORMAL * 0.4)) /
        1000;
      let duration =
        scatterChanceReels[i] === false ? normalDuration : normalDuration + 1;
      reelAnimationsDetails.push({
        duration: duration,
      });
    }

    return {
      scatterPositions: scatterPositions,
      scatterChanceReels: scatterChanceReels,
      reelAnimationsDetails: reelAnimationsDetails,
    };
  }

  async animateSpin(result) {
    this.isSpinSkipped = false;
    const animationDetails = this.calculateAnimationDetails(result);
    return new Promise(async (resolve) => {
      this.drawResultBoard(result);
      this.playSound("start");

      await Promise.all([
        this.spinReel(0, animationDetails),
        this.spinReel(1, animationDetails),
        this.spinReel(2, animationDetails),
        this.spinReel(3, animationDetails),
        this.spinReel(4, animationDetails),
      ]);
      this.sounds["background_slow"].volume(0.5);

      if (this.settings.spinMode === "quick") {
        this.playSound("stop");
      }

      this.reelSpinAnimations = [];

      if (result.scatters.matches) {
        await this.processScattersReels(result.scatters);
      }

      // if (result.bonusPosition.includes(true)) {
      //   await this.processBonusSymbolsReels(result.bonusPosition)
      // }

      if (result.expandedWilds.includes(true)) {
        await this.processWildsReels(result);
      }

      if (result.winAmount > 0) {
        const slotBoard = this.getChildByName("SLOT_BOARD");
        const winLayoutContainer = slotBoard.getChildByName(
          "WIN_LAYOUT_CONTAINER"
        );
        const winAmountText =
          winLayoutContainer.getChildByName("WIN_AMOUNT_TEXT");
        const panelWinAmount = this.getChildByName("UI_CONTAINER")
          .getChildByName("CONTROL_PANEL_CONTAINER")
          .getChildByName("WIN_AMOUNT_TEXT");
        const bonusWinAmount = this.getChildByName("SLOT_BOARD").getChildByName(
          "BONUS_WIN_AMOUNT_TEXT"
        );

        await this.processWinnerLinesSymbols(result.lines);
        winAmountText.text = "";
        const winType = this.getWinType(result.winAmount);
        if (winType !== "NORMAL") {
          gsap.to(winLayoutContainer, {
            duration: 0.2,
            alpha: 1,
          });

          this.showWinSymbol(result.winAmount);
          await this.animateWinAmount(
            winLayoutContainer,
            panelWinAmount,
            winType,
            winAmountText,
            result
          );
        } else {
          await this.animatePanelWinAmount(
            panelWinAmount,
            bonusWinAmount,
            result
          );
        }
      }
      this.setNewReels(result);
      setTimeout(() => {
        resolve(true);
      }, 100);
    });
  }

  setNewReels(res) {
    for (let i = 0; i < 5; i++) {
      const reelObject = this.getChildByName("SLOT_BOARD")
        .getChildByName("REELS_CONTAINER")
        .getChildByName(`REEL_${i}`);
      for (let j = 0; j < 3; j++) {
        reelObject.removeChildAt(reelObject.children.length - 1);
      }
      for (let k = 0; k < 3; k++) {
        let sym = res.board[i][k];
        let symbol = new Symbol(this.resources, {
          num: sym.num,
          index: reelObject.children.length,
          fakeSymbol: true,
        });

        const symbolSprite = symbol.sprite;

        symbolSprite.x = 0;
        symbolSprite.y = reelObject.children.length * 252;

        switch (symbol.spriteName) {
          case "nine":
            symbolSprite.x += 80;
            symbolSprite.y += 59;
            break;
          case "ten":
            symbolSprite.x += 55;
            symbolSprite.y += 48;
            break;
          case "jack":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "queen":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "king":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "ace":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "orchid":
            symbolSprite.x += 30;
            symbolSprite.y += 42;
            break;
          case "vase":
            symbolSprite.x += 30;
            symbolSprite.y += 35;
            break;
          case "knife":
            symbolSprite.x += 30;
            symbolSprite.y += 20;
            break;
          case "horse":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "chengiz":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "wild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "scatter":
            symbolSprite.x += 25;
            symbolSprite.y += 32;
            break;
          case "expandedWild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
        }

        symbolSprite.y -= 252 * 3;

        reelObject.addChild(symbolSprite);
      }
    }
  }

  showWinSymbol(winAmount) {
    let timer;
    const winType = this.getWinType(winAmount);
    if (["NICE", "SUPER", "SENSATIONAL"].includes(winType)) {
      const winTypeSprite = this.getChildByName("SLOT_BOARD")
        .getChildByName("WIN_LAYOUT_CONTAINER")
        .getChildByName(winType);

      winTypeSprite.alpha = 1;
      winTypeSprite.play();
      winTypeSprite.onComplete = () => {
        winTypeSprite.textures = winTypeSprite.textures.reverse();
        winTypeSprite.play();
      };
      timer = setTimeout(() => {
        winTypeSprite.stop();
        winTypeSprite.alpha = 0;
      }, 4000);
    }
  }

  getWinType(winAmount) {
    const totalBetAmount =
      this.settings.betAmount *
      consts.game.multiplierAmount *
      consts.game.chosenLines;
    const multipliedWinAmount = winAmount * consts.game.multiplierAmount;
    if (multipliedWinAmount >= totalBetAmount * 40) return "SENSATIONAL";
    if (
      multipliedWinAmount >= totalBetAmount * 20 &&
      multipliedWinAmount < totalBetAmount * 40
    )
      return "SUPER";
    if (
      multipliedWinAmount >= totalBetAmount * 10 &&
      multipliedWinAmount < totalBetAmount * 20
    )
      return "NICE";

    return "NORMAL";
  }

  setStopButtonVisibility(bool) {
    const stopButton = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("STOP_BUTTON");

    stopButton.visible = bool;
  }

  setStopButtonText(text) {
    const stopButtonText = this.getChildByName("UI_CONTAINER")
      .getChildByName("CONTROL_PANEL_CONTAINER")
      .getChildByName("CONTROL_PANEL_RIGHT_CONTAINER")
      .getChildByName("STOP_BUTTON")
      .getChildByName("STOP_BUTTON_TEXT");
    stopButtonText.text = text;
  }

  onMenuButtonClicked() {
    this.menuController.changeMenuVisibility(
      !this.menuController.showMenuModal
    );
  }

  onBuyBonusButtonClicked() {
    if (
      this.user.chips <
      this.settings.betAmount *
        this.settings.chosenLines *
        this.variationConfig.bonus.multiplier
    ) {
      this.menuController.setAlertModalVisibility(true);
      this.menuController.setAlertModalText("Insufficient balance.");
      return false;
    }
    this.closeAutoContainer();
    this.menuController.changeBuyBonusModalVisibility(
      !this.menuController.showBuyBonusModal
    );
  }

  async handleWheelPopups(type, amount) {
    return await new Promise((resolve) => {
      let winType = "";
      if (type === "multiplier") {
        winType = this.getWinType(amount);
      }
      const wheelContainer = this.getChildByName("WHEEL_CONTAINER");
      const winLayoutContainer = wheelContainer.getChildByName(
        "WIN_LAYOUT_CONTAINER"
      );
      const winAmountText =
        winLayoutContainer.getChildByName("WIN_AMOUNT_TEXT");
      let winSprite;
      switch (type) {
        case "MINOR JACKPOT":
          winSprite = winLayoutContainer.getChildByName("MINOR");
          break;
        case "MAJOR JACKPOT":
          winSprite = winLayoutContainer.getChildByName("MAJOR");
          break;
        case "GRAND JACKPOT":
          winSprite = winLayoutContainer.getChildByName("GRAND");
          break;
        case "multiplier":
          winSprite = winLayoutContainer.getChildByName(winType);
          winAmountText.y -= 80;
          if (winType === "SENSATIONAL") {
            winAmountText.x += 15;
          }
          break;
      }
      winLayoutContainer.alpha = 1;
      this.playSound("win");
      winSprite.alpha = 1;
      winSprite.play();
      winSprite.onComplete = () => {
        winSprite.textures = winSprite.textures.reverse();
        winSprite.play();
      };
      let winAmountAnimation = gsap.fromTo(
        winAmountText,
        { text: "0" },
        {
          duration: 3,
          text: amount,
          onUpdate: () => {
            winAmountText.text = utils.getFormattedAmount(
              Number(winAmountText.text).toFixed(0)
            );
          },
          onComplete: () => {
            setTimeout(() => {
              winSprite.stop();
              winSprite.alpha = 0;
              this.sounds["win"].stop();
              winLayoutContainer.alpha = 0;
              winAmountText.y = 80;
              winAmountText.x = 0;
              document.body.removeEventListener(
                "click",
                clickHandler.bind(this)
              );
              document.body.removeEventListener(
                "keyup",
                clickHandler.bind(this)
              );
              document.body.removeEventListener(
                "touchstart",
                clickHandler.bind(this)
              );
              resolve(true);
            }, 1000);
          },
        }
      );
      const clickHandler = () => {
        winAmountAnimation.totalTime(winAmountAnimation.totalDuration());
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        document.body.removeEventListener(
          "touchstart",
          clickHandler.bind(this)
        );
      };
      document.body.addEventListener("click", clickHandler.bind(this));
      document.body.addEventListener("keyup", clickHandler.bind(this));
      document.body.addEventListener("touchstart", clickHandler.bind(this));
    });
  }

  async animateWinAmount(
    winLayoutContainer,
    panelWinAmount,
    winType,
    winAmountText,
    calculatedSpinResult
  ) {
    let timer;
    return new Promise((resolve) => {
      let panelAnimation;
      switch (winType) {
        case "NICE":
          winAmountText.y = 0;
          winAmountText.x = -15;
          break;
        case "SUPER":
          winAmountText.y = -20;
          winAmountText.x = 15;
          break;
        case "SENSATIONAL":
          winAmountText.y = 0;
          winAmountText.x = 15;
          break;
      }
      if (this.gameType === "normal") {
        gsap.fromTo(
          panelWinAmount,
          { text: "" },
          {
            duration: 0.2,
            text: winType.replace("_", " "),
          }
        );
      } else {
        panelAnimation = gsap.fromTo(
          panelWinAmount,
          { text: this.currentBonusTotalWin },
          {
            duration: 3,
            text: this.currentBonusTotalWin + calculatedSpinResult.winAmount,
            onUpdate: () => {
              panelWinAmount.text = Number(panelWinAmount.text).toFixed(0);
            },
            onComplete: () => {
              panelWinAmount.text = utils.getFormattedAmount(
                Number(panelWinAmount.text).toFixed(0)
              );
            },
          }
        );
      }
      this.playSound("win");
      let symbolAnimation = gsap.fromTo(
        winAmountText,
        { text: "0" },
        {
          duration: 3,
          text: calculatedSpinResult.winAmount,
          onUpdate: () => {
            winAmountText.text = utils.getFormattedAmount(
              Number(winAmountText.text).toFixed(0)
            );
          },
          onComplete: () => {
            timer = setTimeout(() => {
              const winTypeSprite = this.getChildByName("SLOT_BOARD")
                .getChildByName("WIN_LAYOUT_CONTAINER")
                .getChildByName(winType);
              winTypeSprite.stop();
              winTypeSprite.alpha = 0;
              this.sounds["win"].stop();
              winLayoutContainer.alpha = 0;
              document.body.removeEventListener(
                "click",
                clickHandler.bind(this)
              );
              document.body.removeEventListener(
                "keyup",
                clickHandler.bind(this)
              );
              document.body.removeEventListener(
                "touchstart",
                clickHandler.bind(this)
              );
              resolve(true);
            }, 1000);
          },
        }
      );
      const clickHandler = () => {
        if (panelAnimation) {
          panelAnimation.totalTime(panelAnimation.totalDuration());
        }
        symbolAnimation.totalTime(symbolAnimation.totalDuration());
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        document.body.removeEventListener(
          "touchstart",
          clickHandler.bind(this)
        );
      };
      document.body.addEventListener("click", clickHandler.bind(this));
      document.body.addEventListener("keyup", clickHandler.bind(this));
      document.body.addEventListener("touchstart", clickHandler.bind(this));
    });
  }

  async animatePanelWinAmount(panelWinAmount, bonusWinAmount, result) {
    return new Promise((resolve) => {
      this.playSound("normal-win");
      if (this.gameType === "normal") {
        gsap.fromTo(
          panelWinAmount,
          { text: "0" },
          {
            duration: 1,
            text: result.winAmount,
            onUpdate: () => {
              panelWinAmount.text = utils.getFormattedAmount(
                Number(panelWinAmount.text).toFixed(0)
              );
            },
            onComplete: () => {
              setTimeout(() => {
                this.sounds["normal-win"].stop();
                resolve(true);
              }, 500);
            },
          }
        );
      } else {
        bonusWinAmount.visible = true;
        bonusWinAmount.text = utils.getFormattedAmount(
          Number(result.winAmount).toFixed(0)
        );
        gsap.fromTo(
          bonusWinAmount,
          {
            pixi: {
              y: 0,
              alpha: 1,
            },
          },
          {
            duration: 2,
            pixi: {
              y: 500,
              alpha: 0,
            },
            onComplete: () => {
              bonusWinAmount.visible = false;
            },
          }
        );
        gsap.fromTo(
          panelWinAmount,
          { text: this.currentBonusTotalWin },
          {
            duration: 2,
            text: this.currentBonusTotalWin + result.winAmount,
            onUpdate: () => {
              panelWinAmount.text = Number(panelWinAmount.text).toFixed(0);
            },
            onComplete: () => {
              panelWinAmount.text = utils.getFormattedAmount(
                Number(panelWinAmount.text).toFixed(0)
              );
              setTimeout(() => {
                resolve(true);
              }, 500);
            },
          }
        );
      }
    });
  }

  createReels(reelType) {
    this.oncePlayed = false;
    const reels = [];
    for (let i = 0; i < 5; i++) {
      reels.push(
        new Reel(this.variationConfig, this.resources, reelType, {
          index: i,
        })
      );
    }

    const reelsContainer =
      this.getChildByName("SLOT_BOARD").getChildByName("REELS_CONTAINER");

    reels.forEach((reel) => {
      reelsContainer.addChild(reel);
    });

    const slotBoard = this.getChildByName("SLOT_BOARD");

    const FrameAnimationsContainer = new Container();
    FrameAnimationsContainer.name = "FRAMES_ANIMATIONS_CONTAINER";
    FrameAnimationsContainer.x = -695;
    FrameAnimationsContainer.y = -290;
    FrameAnimationsContainer.scale.set(1, 1);
    slotBoard.addChild(FrameAnimationsContainer);

    if (reelType === "normal") {
      for (let i = 2; i < 5; i++) {
        let scatterFrameAnimation = new AnimatedSprite(
          this.resources["scatter-reel-frame"].spritesheet.animations[
            "scatter-reel-frame"
          ]
        );
        scatterFrameAnimation.name = `SCATTER_REEL_FRAME_${i}`;
        scatterFrameAnimation.loop = false;
        scatterFrameAnimation.animationSpeed = 0.3;
        scatterFrameAnimation.x = i * 250;
        scatterFrameAnimation.alpha = 0;
        FrameAnimationsContainer.addChild(scatterFrameAnimation);
      }
    }
  }

  async spinReel(index, animationDetails) {
    return await new Promise((resolve) => {
      const reel = this.getChildByName("SLOT_BOARD")
        .getChildByName("REELS_CONTAINER")
        .getChildByName(`REEL_${index}`);

      if (reel.isMask) return;

      let duration;
      switch (this.settings.spinMode) {
        case consts.game.spinMode.QUICK:
          duration =
            (consts.game.spinModeDuration.QUICK +
              consts.game.spinModeDuration.QUICK * 0.2) /
            1000;
          break;
        default:
          duration =
            (consts.game.spinModeDuration.NORMAL +
              index * (consts.game.spinModeDuration.NORMAL * 0.4)) /
            1000;
          break;
      }

      let tl = new gsap.timeline();
      if (
        animationDetails.scatterChanceReels[index] &&
        this.settings.spinMode === "normal"
      ) {
        const firstScatterReelPosition =
          animationDetails.scatterChanceReels.indexOf(true);
        tl.fromTo(
          reel,
          {
            pixi: {
              y: (reel.children.length - 3) * 252 * -1 + 600,
            },
          },
          {
            duration: duration,
            pixi: {
              y: `+=${reel.children.length * 252 - 150 - 600}`,
            },
            repeat: index - firstScatterReelPosition,
            ease: "none",
          }
        );
      }
      tl.fromTo(
        reel,
        {
          pixi: {
            y: (reel.children.length - 3) * 252 * -1 + 600,
            filters: [new MotionBlurFilter([0, 7], 7)],
          },
        },
        {
          duration:
            this.settings.spinMode === "normal"
              ? animationDetails.reelAnimationsDetails[index].duration
              : duration,
          pixi: {
            y: `+=${reel.children.length * 252 - 150 - 600}`,
          },
          ease: CustomEase.create(
            "custom",
            "M0,0 C0.266,0.412 0.393,0.529 0.522,0.65 0.556,0.681 0.753,0.859 0.862,0.976 0.908,1.026 0.951,1 1,1 "
          ),
          onComplete: () => {
            reel.filters = [];
            this.processFrameAnimations(
              index,
              animationDetails.scatterPositions
            );
            if (this.settings.spinMode === "normal") {
              this.playSound("stop");
            }
            document.body.removeEventListener("click", clickHandler.bind(this));
            document.body.removeEventListener("keyup", clickHandler.bind(this));
            document.body.removeEventListener(
              "touchstart",
              clickHandler.bind(this)
            );
            setTimeout(() => {
              resolve(true);
            }, 100);
          },
        }
      );
      const clickHandler = () => {
        tl.timeScale(7);
        this.isSpinSkipped = true;
        document.body.removeEventListener("click", clickHandler.bind(this));
        document.body.removeEventListener("keyup", clickHandler.bind(this));
        document.body.removeEventListener(
          "touchstart",
          clickHandler.bind(this)
        );
      };
      if (this.settings.spinMode === "normal") {
        document.body.addEventListener("click", clickHandler.bind(this));
        document.body.addEventListener("keyup", clickHandler.bind(this));
        document.body.addEventListener("touchstart", clickHandler.bind(this));
      }
      this.reelSpinAnimations.push(tl);
    });
  }

  processFrameAnimations(index, scatterPositions) {
    if (scatterPositions.length >= 2 && this.settings.spinMode === "normal") {
      if (index >= scatterPositions[1]) {
        const framesContainer = this.getChildByName(
          "SLOT_BOARD"
        ).getChildByName("FRAMES_ANIMATIONS_CONTAINER");
        const currentReel = framesContainer.getChildByName(
          `SCATTER_REEL_FRAME_${index}`
        );
        const nextReel = framesContainer.getChildByName(
          `SCATTER_REEL_FRAME_${index + 1}`
        );

        if (currentReel !== null) {
          currentReel.gotoAndStop(0);
          currentReel.alpha = 0;
        }
        if (nextReel !== null && !this.isSpinSkipped) {
          nextReel.alpha = 1;
          nextReel.play();
          this.sounds["background_slow"].volume(0.2);
          this.playSound("reel-frame");
          if (typeof nextReel.onComplete !== "undefined") {
            nextReel.onComplete = () => {
              nextReel.gotoAndPlay(36);
            };
          }
        }
      }
    }
  }

  async processWinnerLinesSymbols(lines = []) {
    return new Promise((resolve) => {
      let playingSounds = [];
      lines.forEach((line, lineIndex) => {
        if (line === null) return;
        const lineData = this.variationConfig.lines[lineIndex];

        for (let i = 0; i < line.matches; i++) {
          const symbolIndex = lineData[i];
          const reel = this.getChildByName("SLOT_BOARD")
            .getChildByName("REELS_CONTAINER")
            .getChildByName(`REEL_${i}`);
          const symbol = reel.getChildAt(symbolIndex);
          const firstSymbolIndex = this.getChildByName("SLOT_BOARD")
            .getChildByName("REELS_CONTAINER")
            .getChildByName("REEL_0")
            .getChildAt(lineData[0]).num;
          if (symbol.alpha > 0) symbol.alpha = 1;
          if (!["scatter", "expandedWild"].includes(symbol.name)) {
            symbol.play();
            symbol.loop = true;
            if (
              !playingSounds.includes(symbol.name) &&
              (symbol.num === firstSymbolIndex || symbol.num === 11)
            ) {
              if (symbol.num < 6) {
                if (!playingSounds.includes("royals")) {
                  this.playSound("royals");
                  playingSounds.push("royals");
                }
              } else {
                this.playSound(symbol.name);
                playingSounds.push(symbol.name);
              }
            }
          }
        }
      });
      setTimeout(() => {
        resolve(true);
      }, 200);
    });
  }

  playSound(trackName) {
    const symbolsSound = this.sounds[trackName];
    if (typeof symbolsSound === "undefined") return false;
    if (trackName === "win" || trackName === "normal-win") {
      symbolsSound.volume(0.8);
    } else {
      symbolsSound.volume(1);
    }
    symbolsSound.play();
  }

  async processWildsReels(result) {
    return new Promise((resolve) => {
      result.expandedWilds.forEach((wild, reelIndex) => {
        if (!wild || result.bonusPosition[reelIndex]) return false;
        let reel = this.getChildByName("SLOT_BOARD")
          .getChildByName("REELS_CONTAINER")
          .getChildByName(`REEL_${reelIndex}`);

        for (let i = 0; i < 3; i++) {
          const symbol = reel.getChildAt(i);
          if (symbol.name !== "expandedWild") {
            symbol.alpha = 0;
          } else if (symbol.name === "expandedWild") {
            switch (i) {
              case 1:
                gsap.to(symbol, {
                  duration: 0.15,
                  pixi: {
                    y: symbol.y - 252,
                  },
                });
                break;
              case 2:
                gsap.to(symbol, {
                  duration: 0.15,
                  pixi: {
                    y: symbol.y - 504,
                  },
                });
                break;
            }
            if (!this.sounds["wild"].playing()) {
              this.playSound("wild");
            }
            const wildSymbol = reel.getChildAt(i);
            wildSymbol.play();
            wildSymbol.onComplete = () => {
              wildSymbol.gotoAndPlay(39);
            };
          }
        }
      });
      setTimeout(() => {
        resolve(true);
      }, 1300);
    });
  }

  async processScattersReels(scatters) {
    const scatterReels = scatters.positions;
    return new Promise((resolve) => {
      scatterReels.forEach((inReelPositions, reelIndex) => {
        if (inReelPositions.length === 0) return false;
        let reel = this.getChildByName("SLOT_BOARD")
          .getChildByName("REELS_CONTAINER")
          .getChildByName(`REEL_${reelIndex}`);

        for (let i = 0; i < 3; i++) {
          const symbol = reel.getChildAt(i);
          if (symbol.name === "scatter") {
            if (!this.sounds["freespins"].playing()) {
              this.playSound("freespins");
            }
            symbol.play();
            symbol.loop = true;
          }
        }
      });
      setTimeout(() => {
        resolve(true);
      }, 1300);
    });
  }

  turnOffAllSymbols() {
    const reels =
      this.getChildByName("SLOT_BOARD").getChildByName("REELS_CONTAINER");

    reels.children.forEach((reel) => {
      reel.children.forEach((symbol) => {
        if (symbol.alpha > 0) symbol.alpha = 0.6;
      });
    });
  }

  turnOnAllSymbols() {
    const reels =
      this.getChildByName("SLOT_BOARD").getChildByName("REELS_CONTAINER");

    reels.children.forEach((reel) => {
      reel.children.forEach((symbol) => {
        if (symbol.alpha > 0) symbol.alpha = 1;
      });
    });
  }

  calculateSpinResult(result = [], gameType) {
    const board = [];
    const reels =
      gameType === "normal"
        ? this.variationConfig.reels[0]
        : this.variationConfig.reels[1];
    result.reelPositions.forEach((item, itemIndex) => {
      board.push([]);
      for (let i = 0; i < 3; i++) {
        const to = (item + i) % reels[itemIndex].length;
        const symbol = consts.assets.symbol.animatedSprites.find(
          (sym) => sym.num === reels[itemIndex][to]
        );
        board[itemIndex].push(symbol);
      }
    });

    let bonusPosition = [false, false, false, false, false];
    if (result.wins.bonuses) {
      for (let i = 0; i < board.length; i++) {
        for (let j = 0; j < board[i].length; j++) {
          if (board[i][j].num > 11) {
            bonusPosition[i] = true;
          }
        }
      }
    }

    if (typeof result.wins.scatters == "undefined")
      result.wins.scatters = { positions: [] };
    if (typeof result.wins.bonuses == "undefined") result.wins.bonuses = [];

    if (result.wins.bonuses.length > 0) {
      if (result.wins.bonuses[0].amount) {
        result.win -= result.wins.bonuses[0].amount;
      }
    }
    return {
      board,
      winAmount: result.win,
      scatters: result.wins.scatters,
      expandedWilds: result.wins.expandedWilds,
      lines: result.wins.lines,
      bonusPosition: bonusPosition,
      bonuses: result.wins.bonuses,
    };
  }

  drawResultBoard(res) {
    res.board.forEach((reel, reelIndex) => {
      const reelObject = this.getChildByName("SLOT_BOARD")
        .getChildByName("REELS_CONTAINER")
        .getChildByName(`REEL_${reelIndex}`);
      let lastIndex = 0;
      reel.forEach((symbolData, symbolIndex) => {
        let symbol = new Symbol(this.resources, {
          num: symbolData.num,
          index: symbolIndex,
          fakeSymbol: false,
        });

        const symbolSprite = symbol.sprite;

        symbolSprite.x = 0;
        symbolSprite.y = symbolIndex * 252;

        switch (symbol.spriteName) {
          case "nine":
            symbolSprite.x += 80;
            symbolSprite.y += 59;
            break;
          case "ten":
            symbolSprite.x += 55;
            symbolSprite.y += 48;
            break;
          case "jack":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "queen":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "king":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "ace":
            symbolSprite.x += 60;
            symbolSprite.y += 52;
            break;
          case "orchid":
            symbolSprite.x += 30;
            symbolSprite.y += 42;
            break;
          case "vase":
            symbolSprite.x += 30;
            symbolSprite.y += 35;
            break;
          case "knife":
            symbolSprite.x += 30;
            symbolSprite.y += 20;
            break;
          case "horse":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "chengiz":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "wild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
          case "scatter":
            symbolSprite.x += 25;
            symbolSprite.y += 32;
            break;
          case "expandedWild":
            symbolSprite.x += 25;
            symbolSprite.y += 25;
            break;
        }

        symbolSprite.y -= 252 * 3;

        reelObject.addChildAt(symbolSprite, lastIndex);
        lastIndex++;
      });
    });
  }

  onResize(width, height) {
    const slotBoard = this.getChildByName("SLOT_BOARD");
    slotBoard.x = width / 2;
    slotBoard.y = height / 2;
    slotBoard.y -= utils.isMobile ? height * 0.035 : height * 0.035;

    const slotBoardRatio =
      Math.min(width / 1638, height / 1071) - (utils.isMobile ? 0.04 : 0.1);
    slotBoard.scale.set(slotBoardRatio, slotBoardRatio);

    const uiContainer = this.getChildByName("UI_CONTAINER");

    const controlPanelContainer = uiContainer.getChildByName(
      "CONTROL_PANEL_CONTAINER"
    );
    controlPanelContainer.y = window.innerHeight;
    const controlPanelRatio = window.innerWidth / consts.game.width;
    controlPanelContainer.scale.set(controlPanelRatio, controlPanelRatio);

    const balanceContainer = uiContainer.getChildByName("BALANCE_CONTAINER");
    const balanceContainerRatio = window.innerWidth / consts.game.width;
    balanceContainer.scale.set(balanceContainerRatio, balanceContainerRatio);

    const menuContainer = uiContainer.getChildByName("MENU_CONTAINER");
    const menuContainerRatio = window.innerWidth / consts.game.width;
    menuContainer.scale.set(menuContainerRatio, menuContainerRatio);

    const buyBonusContainer = this.getChildByName("BUY_BONUS_CONTAINER");
    const buyBonusButton = buyBonusContainer.getChildByName("BUY_BONUS_BUTTON");

    const buyBonusRatio = Math.min(
      window.innerWidth / consts.game.width,
      window.innerHeight / consts.game.height
    );
    buyBonusButton.scale.set(buyBonusRatio, buyBonusRatio);

    const wheelContainer = this.getChildByName("WHEEL_CONTAINER");
    const wheelContainerRatio = Math.min(width / 1920, height / 1080);
    wheelContainer.x = width / 2;
    wheelContainer.y = height / 2;
    wheelContainer.scale.set(wheelContainerRatio, wheelContainerRatio);

    const curtainContainer = this.getChildByName("CURTAIN_CONTAINER");
    const curtainBg = curtainContainer.getChildByName("CURTAIN_BG");
    const curtainRightPiece = curtainContainer.getChildByName(
      "CURTAIN_RIGHT_PIECE"
    );
    const curtainLeftPiece =
      curtainContainer.getChildByName("CURTAIN_LEFT_PIECE");

    curtainRightPiece.x = width;
    curtainRightPiece.width = width / 2 + 36;
    curtainRightPiece.height = height;
    curtainLeftPiece.width = width / 2 + 36;
    curtainLeftPiece.height = height;
    curtainBg.width = width;
    curtainBg.height = height;
  }
}
